import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import patientsApi from "../../api/patientsApi";
import followCheckUpsApi from "../../api/followCheckUpsApi";
import checkUpReviewsApi from "../../api/checkUpReviewsApi";
import companyBranchesApi from "../../api/companyBranchesApi";
import styles from "./NutriologyDashboard.module.css";
import logo from "../../assets/logo.png";

const NAV_ITEMS = [
  { key: "general", label: "Datos Generales", icon: "general" },
  { key: "generalEvaluation", label: "Evaluacion general", icon: "general" },
  { key: "patients", label: "Pacientes", icon: "patients" },
  { key: "appointments", label: "Primeras citas", icon: "appointments" },
  { key: "followAppointments", label: "Citas Seguimiento", icon: "appointments" },
  { key: "historicalUpload", label: "Subir historico", icon: "general" },
];

const SECTION_COPY = {
  general: {
    title: "Datos Generales",
    subtitle: "Resumen operativo del módulo de nutriología.",
    empty: "Sin datos generales para mostrar.",
  },
  generalEvaluation: {
    title: "Evaluacion general",
    subtitle: "Resultados de evaluaciones y comentarios de las consultas.",
    empty: "Sin evaluaciones para mostrar.",
  },
  patients: {
    title: "Pacientes",
    subtitle: "Directorio y seguimiento de pacientes.",
    empty: "Sin pacientes para mostrar.",
  },
  appointments: {
    title: "Primeras citas",
    subtitle: "Agenda y captura de primeras consultas nutricionales.",
    empty: "Sin citas para mostrar.",
  },
  followAppointments: {
    title: "Citas Seguimiento",
    subtitle: "Consultas posteriores para pacientes con primera cita registrada.",
    empty: "Sin citas de seguimiento para mostrar.",
  },
  historicalUpload: {
    title: "Subir historico",
    subtitle: "Carga masiva de pacientes, primeras consultas y seguimientos desde XLSX.",
    empty: "Selecciona un archivo para revisar la carga.",
  },
};
const CONSULTATION_REASON_OPTIONS = [
  "Mejorar composición corporal",
  "Cambio de hábitos",
  "Mejorar relación con comida",
  "Hacer ejercicio",
  "Corrección bioquímicos",
  "Disminuir estrés",
  "Referido por medicina",
];
const PATIENT_OBJECTIVE_OPTIONS = [
  "Bajar de peso",
  "Mantener peso",
  "Subir peso",
  "Aumentar Masa Muscular",
  "Bajar % de grasa o GV",
];
const MODALITY_OPTIONS = ["Presencial", "Virtual"];
const COMPLIANCE_OPTIONS = ["0", "1", "2", "3", "4", "5"];
const LACK_OF_ADHERENCE_OPTIONS = [
  "Razones personales",
  "Razones relacionadas al trabajo",
  "Otro",
  "N/A",
  "si se apega",
];
const CANCELLATION_REASON_OPTIONS = [
  { value: "reagendada", label: "Reagendada" },
  { value: "inasistencia", label: "Inasistencia" },
];
const TABLE_PAGE_SIZE = 15;
const APPOINTMENT_STATUS_FILTER_OPTIONS = [
  { value: "cancelled", label: "Cancelada" },
  { value: "evaluated", label: "Registrada" },
  { value: "pending", label: "Pendiente" },
  { value: "waiting_for_data", label: "Esperando datos" },
];
const EMPTY_PATIENT_TABLE_FILTERS = {
  name: "",
  gender: "",
  email: "",
  jobArea: "",
};
const EMPTY_APPOINTMENT_TABLE_FILTERS = {
  patient: "",
  startDate: "",
  endDate: "",
  status: "",
  email: "",
  jobArea: "",
};
const ACTIVITY_FACTOR_OPTIONS = [
  { value: "1.2", label: "Sedentario", detail: "Poco o nada de ejercicio" },
  { value: "1.375", label: "Ligeramente activo", detail: "Ejercicio ligero 1–3 días por semana" },
  { value: "1.55", label: "Moderadamente activo", detail: "Ejercicio moderado 3–5 días por semana" },
  { value: "1.725", label: "Muy activo", detail: "Ejercicio intenso 6–7 días por semana" },
  { value: "1.9", label: "Extremadamente activo", detail: "Ejercicio muy intenso o trabajo físico pesado" },
];
const HISTORICAL_TABS = [
  { key: "patients", label: "Pacientes" },
  { key: "firstCheckUps", label: "Primeras citas" },
  { key: "followCheckUps", label: "Seguimientos" },
];

const EMPTY_PATIENT_FORM = {
  branchId: "",
  name: "",
  gender: "",
  email: "",
  externalId: "",
  birthDate: "",
  jobArea: "",
  comments: "",
};
const EMPTY_APPOINTMENT_FORM = {
  patientId: "",
  patientMode: "existing",
  newPatient: { ...EMPTY_PATIENT_FORM },
  date: "",
  modality: "",
  consultationReasons: "",
  patientObjective: "",
  height: "",
  weight: "",
  targetWeight: "",
  bodyFatPercentage: "",
  visceralFat: "",
  musclePercentage: "",
  waistCm: "",
  notes: "",
  medicalDiagnoses: "",
  planSent: false,
};
const EMPTY_FOLLOW_FORM = {
  patientId: "",
  date: "",
  modality: "",
  weight: "",
  bodyFatPercentage: "",
  visceralFat: "",
  musclePercentage: "",
  waistCm: "",
  compliance: "",
  comments: "",
  lackOfAdherenceReason: "",
  planSent: false,
};
const EMPTY_SUMMARY = {
  patientsCount: 0,
  malePatientsCount: 0,
  femalePatientsCount: 0,
  firstCheckUpsCount: 0,
  followCheckUpsCount: 0,
  monthly: [],
};
const EMPTY_IMC_DISTRIBUTION = [
  { name: "Normal", value: 0 },
  { name: "Sobrepeso", value: 0 },
  { name: "Obesidad I", value: 0 },
  { name: "Obesidad II", value: 0 },
  { name: "Obesidad III", value: 0 },
  { name: "Sin registro", value: 0 },
];
const EMPTY_BODY_FAT_DISTRIBUTION = [
  { name: "Alto", value: 0 },
  { name: "Normal", value: 0 },
  { name: "Sin registro", value: 0 },
];
const EMPTY_WAIST_DISTRIBUTION = [
  { name: "Alto riesgo", value: 0 },
  { name: "Riesgo", value: 0 },
  { name: "Sin Riesgo", value: 0 },
  { name: "Sin registro", value: 0 },
];
const EMPTY_VISCERAL_FAT_DISTRIBUTION = [
  { name: "Con riesgo", value: 0 },
  { name: "Sin riesgo", value: 0 },
  { name: "Sin registro", value: 0 },
];
const CHART_COLORS = {
  teal: "#00be9c",
  blue: "#1069f1",
  amber: "#f4b740",
  green: "#00be9c",
  yellow: "#f4b740",
  overweightYellow: "#ffe951",
  red: "#dc3545",
};
const TOTALS_BAR_SERIES = [
  { key: "totalConsultationsCount", label: "Consultas", color: CHART_COLORS.yellow },
  { key: "firstCheckUpsCount", label: "Primeras", color: CHART_COLORS.blue },
  { key: "followCheckUpsCount", label: "Seguimientos", color: CHART_COLORS.green },
  { key: "cancelledConsultationsCount", label: "Canceladas", color: CHART_COLORS.red },
];
const GENDER_BAR_SERIES = [
  { key: "consultationsCount", label: "Consultas totales", color: CHART_COLORS.overweightYellow },
  { key: "uniquePatientsCount", label: "Pacientes únicos", color: CHART_COLORS.teal },
  { key: "malePatientsCount", label: "Hombres", color: CHART_COLORS.blue },
  { key: "femalePatientsCount", label: "Mujeres", color: CHART_COLORS.amber },
];
const REVIEW_AVERAGE_SERIES = [
  { key: "average", label: "Promedio", color: CHART_COLORS.blue },
];
const REVIEW_QUESTION_SERIES = [
  { key: "generalConsultationScore", label: "Consulta general", color: CHART_COLORS.blue },
  { key: "nutritionistCareScore", label: "Atencion y trato", color: CHART_COLORS.teal },
  { key: "mealPlanExplanationScore", label: "Explicacion del plan", color: CHART_COLORS.amber },
];
const EMPTY_REVIEW_ANALYTICS = {
  count: 0,
  questions: [],
  monthly: [],
  comments: [],
};
const IMC_STACK_ORDER = ["Obesidad III", "Obesidad II", "Obesidad I", "Sobrepeso", "Normal", "Sin registro"];
const IMC_EVOLUTION_LINE_ORDER = IMC_STACK_ORDER.filter((category) => category !== "Sin registro");
const IMC_STACK_COLORS = {
  "Obesidad III": "#dc3545",
  "Obesidad II": "#fd7e14",
  "Obesidad I": "#ffa040",
  Sobrepeso: "#ffe951",
  Normal: "#00be9c",
  "Sin registro": "#a9b4bd",
};
const BODY_FAT_STACK_ORDER = ["Alto", "Normal"];
const BODY_FAT_STACK_COLORS = {
  Alto: "#fd7e14",
  Normal: "#00be9c"
};
const WAIST_STACK_ORDER = ["Alto riesgo", "Riesgo", "Sin Riesgo"];
const WAIST_STACK_COLORS = {
  "Alto riesgo": "#dc3545",
  Riesgo: "#fd7e14",
  "Sin Riesgo": "#00be9c"
};

const VISCERAL_FAT_STACK_ORDER = ["Con riesgo", "Sin riesgo"];
const VISCERAL_FAT_STACK_COLORS = {
  "Con riesgo": "#fd7e14",
  "Sin riesgo": "#00be9c",
};

const DIAGNOSIS_SCOPE_OPTIONS = [
  { value: "global", label: "General" },
  { value: "masculino", label: "Masculino" },
  { value: "femenino", label: "Femenino" },
];
const EMPTY_CHART_JOB_AREA_FILTERS = {
  totals: "",
  gender: "",
  imc: "",
  bodyFat: "",
  waist: "",
  visceralFat: "",
  reviewAverage: "",
  reviewQuestions: "",
};
const MONTHS = [
  { index: 0, value: "01", short: "Ene", label: "Enero", quarter: 1 },
  { index: 1, value: "02", short: "Feb", label: "Febrero", quarter: 1 },
  { index: 2, value: "03", short: "Mar", label: "Marzo", quarter: 1 },
  { index: 3, value: "04", short: "Abr", label: "Abril", quarter: 2 },
  { index: 4, value: "05", short: "May", label: "Mayo", quarter: 2 },
  { index: 5, value: "06", short: "Jun", label: "Junio", quarter: 2 },
  { index: 6, value: "07", short: "Jul", label: "Julio", quarter: 3 },
  { index: 7, value: "08", short: "Ago", label: "Agosto", quarter: 3 },
  { index: 8, value: "09", short: "Sep", label: "Septiembre", quarter: 3 },
  { index: 9, value: "10", short: "Oct", label: "Octubre", quarter: 4 },
  { index: 10, value: "11", short: "Nov", label: "Noviembre", quarter: 4 },
  { index: 11, value: "12", short: "Dic", label: "Diciembre", quarter: 4 },
];
const QUARTERS = [
  { value: 1, label: "T1", months: [0, 1, 2] },
  { value: 2, label: "T2", months: [3, 4, 5] },
  { value: 3, label: "T3", months: [6, 7, 8] },
  { value: 4, label: "T4", months: [9, 10, 11] },
];
const CURRENT_DATE = new Date();
const CURRENT_YEAR = CURRENT_DATE.getFullYear();
const CURRENT_MONTH_INDEX = CURRENT_DATE.getMonth();
const CURRENT_QUARTER = Math.floor(CURRENT_MONTH_INDEX / 3) + 1;
const PUBLIC_EMAIL_DOMAINS = new Set([
  "gmail.com",
  "googlemail.com",
  "yahoo.com",
  "yahoo.com.mx",
  "hotmail.com",
  "hotmail.com.mx",
  "outlook.com",
  "outlook.com.mx",
  "live.com",
  "live.com.mx",
  "msn.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "aol.com",
  "proton.me",
  "protonmail.com",
  "zoho.com",
  "gmx.com",
  "gmx.net",
  "mail.com",
  "yandex.com",
]);

function getEmailDomain(value) {
  const parts = String(value || "").trim().toLowerCase().split("@");
  return parts.length === 2 ? parts[1] : "";
}

function isPublicEmail(value) {
  const domain = getEmailDomain(value);
  return Boolean(domain && PUBLIC_EMAIL_DOMAINS.has(domain));
}

function getTokenBranches() {
  try {
    const token = localStorage.getItem("token");
    if (!token) return [];
    const decoded = jwtDecode(token);
    return Array.isArray(decoded.branches) ? decoded.branches.map(String) : [];
  } catch (_) {
    return [];
  }
}

function getTokenUserId() {
  try {
    const token = localStorage.getItem("token");
    if (!token) return "";
    const decoded = jwtDecode(token);
    return decoded.userId || decoded._id || decoded.id || "";
  } catch (_) {
    return "";
  }
}

function formatGender(gender) {
  if (gender === "femenino") return "Femenino";
  if (gender === "masculino") return "Masculino";
  return "Sin registro";
}

function normalizeFilterText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("es-MX")
    .trim();
}

function getLocalDateKey(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getAppointmentStatusValue(appointment, { followUp = false } = {}) {
  if (isAppointmentCancelled(appointment)) return "cancelled";
  return appointment?.status || (followUp ? "evaluated" : "pending");
}

function getUniqueOptions(values) {
  return [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b, "es-MX"));
}

function filterAppointmentRows(rows, filters, { followUp = false } = {}) {
  const patientSearch = normalizeFilterText(filters.patient);
  const emailSearch = normalizeFilterText(filters.email);

  return rows.filter((appointment) => {
    const dateKey = getLocalDateKey(appointment.date);
    const status = getAppointmentStatusValue(appointment, { followUp });
    return (
      (!patientSearch || normalizeFilterText(appointment.patientName).includes(patientSearch)) &&
      (!emailSearch || normalizeFilterText(appointment.patientEmail).includes(emailSearch)) &&
      (!filters.jobArea || appointment.patientJobArea === filters.jobArea) &&
      (!filters.status || status === filters.status) &&
      (!filters.startDate || dateKey >= filters.startDate) &&
      (!filters.endDate || dateKey <= filters.endDate)
    );
  });
}

function calculateCaloricExpenditure({ formula, gender, weight, height, age, activityFactor }) {
  const numericWeight = Number(weight);
  const numericHeight = Number(height);
  const numericAge = Number(age);
  const numericActivityFactor = Number(activityFactor);
  const heightCm = numericHeight <= 3 ? numericHeight * 100 : numericHeight;

  if (
    ![numericWeight, numericHeight, numericAge, numericActivityFactor].every(Number.isFinite) ||
    numericWeight <= 0 ||
    numericHeight <= 0 ||
    numericAge <= 0 ||
    numericActivityFactor <= 0 ||
    !["masculino", "femenino"].includes(gender)
  ) {
    return null;
  }

  let basalRate;
  if (formula === "harris-benedict") {
    basalRate = gender === "masculino"
      ? 88.362 + (13.397 * numericWeight) + (4.799 * heightCm) - (5.677 * numericAge)
      : 447.593 + (9.247 * numericWeight) + (3.098 * heightCm) - (4.33 * numericAge);
  } else {
    basalRate = gender === "masculino"
      ? (10 * numericWeight) + (6.25 * heightCm) - (5 * numericAge) + 5
      : (10 * numericWeight) + (6.25 * heightCm) - (5 * numericAge) - 161;
  }

  return {
    basalRate: Math.round(basalRate * 10) / 10,
    totalExpenditure: Math.round(basalRate * numericActivityFactor),
  };
}

function formatDateTime(value) {
  if (!value) return "Sin registro";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sin registro";
  return new Intl.DateTimeFormat("es-MX", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatStatus(status) {
  const labels = {
    pending: "Pendiente",
    cancelled: "Cancelada",
    evaluated: "Registrada",
    waiting_for_data: "Esperando datos",
  };
  return labels[status] || "Sin estado";
}

function isAppointmentCancelled(appointment) {
  return Boolean(appointment?.isCancelled || appointment?.status === "cancelled");
}

function formatCancellationReason(reason) {
  return CANCELLATION_REASON_OPTIONS.find((option) => option.value === reason)?.label || "Sin motivo";
}

function normalizeDateTimeLocal(value) {
  if (!value) return "";
  return new Date(value).toISOString();
}

function formatDateTimeLocalInput(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 16);
}

function splitMultiValue(value) {
  return value ? String(value).split(",").map((item) => item.trim()).filter(Boolean) : [];
}

function getCheckUpDraft(checkup) {
  return {
    date: formatDateTimeLocalInput(checkup.date),
    age: checkup.age ?? checkup.patientAge ?? "",
    modality: checkup.modality || "",
    consultationReasons: checkup.consultationReasons || "",
    patientObjective: checkup.patientObjective || "",
    height: checkup.height ?? "",
    weight: checkup.weight ?? "",
    calConsumption: checkup.calConsumption ?? "",
    activityFactor: "",
    calorieFormula: "",
    targetWeight: checkup.targetWeight ?? "",
    bodyFatPercentage: checkup.bodyFatPercentage ?? "",
    visceralFat: checkup.visceralFat ?? "",
    musclePercentage: checkup.musclePercentage ?? "",
    waistCm: checkup.waistCm ?? "",
    notes: checkup.notes || "",
    medicalDiagnoses: checkup.medicalDiagnoses || "",
    planSent: Boolean(checkup.planSent),
  };
}

function getCheckUpPayloadFromDraft(draft) {
  return {
    date: normalizeDateTimeLocal(draft.date),
    age: draft.age,
    modality: draft.modality,
    consultationReasons: draft.consultationReasons,
    patientObjective: draft.patientObjective,
    height: draft.height,
    weight: draft.weight,
    calConsumption: draft.calConsumption,
    targetWeight: draft.targetWeight,
    bodyFatPercentage: draft.bodyFatPercentage,
    visceralFat: draft.visceralFat,
    musclePercentage: draft.musclePercentage,
    waistCm: draft.waistCm,
    notes: draft.notes,
    medicalDiagnoses: draft.medicalDiagnoses,
    planSent: draft.planSent,
  };
}

function getFollowDraft(followCheckUp) {
  return {
    date: formatDateTimeLocalInput(followCheckUp.date),
    modality: followCheckUp.modality || "",
    weight: followCheckUp.weight ?? "",
    bodyFatPercentage: followCheckUp.bodyFatPercentage ?? "",
    visceralFat: followCheckUp.visceralFat ?? "",
    musclePercentage: followCheckUp.musclePercentage ?? "",
    waistCm: followCheckUp.waistCm ?? "",
    compliance: followCheckUp.compliance ?? "",
    comments: followCheckUp.comments || "",
    lackOfAdherenceReason: followCheckUp.lackOfAdherenceReason || "",
    planSent: Boolean(followCheckUp.planSent),
  };
}

function getFollowPayloadFromDraft(draft, patientId) {
  return {
    patientId,
    date: normalizeDateTimeLocal(draft.date),
    modality: draft.modality,
    weight: draft.weight,
    bodyFatPercentage: draft.bodyFatPercentage,
    visceralFat: draft.visceralFat,
    musclePercentage: draft.musclePercentage,
    waistCm: draft.waistCm,
    compliance: draft.compliance,
    comments: draft.comments,
    lackOfAdherenceReason: draft.lackOfAdherenceReason,
    planSent: draft.planSent,
  };
}

function formatCheckUpValue(value, suffix = "") {
  if (value === undefined || value === null || value === "") return "Sin registro";
  return `${value}${suffix}`;
}

function getAvailableYears() {
  return Array.from({ length: 6 }, (_, index) => CURRENT_YEAR - index);
}

function getAvailableQuarters(year) {
  if (Number(year) === CURRENT_YEAR) {
    return QUARTERS.filter((quarter) => quarter.value <= CURRENT_QUARTER);
  }
  return QUARTERS;
}

function getAvailableMonthsForYear(year) {
  return MONTHS.filter((month) => Number(year) < CURRENT_YEAR || month.index <= CURRENT_MONTH_INDEX);
}

function getMonthsForQuarters(year, quarters) {
  const quarterSet = new Set(quarters.map(Number));
  return getAvailableMonthsForYear(year).filter((month) => quarterSet.has(month.quarter));
}

function getDefaultQuartersForYear(year) {
  const availableQuarters = getAvailableQuarters(year);
  const latestQuarter = availableQuarters[availableQuarters.length - 1]?.value || 1;
  return [latestQuarter];
}

function getMonthKey(year, monthValue) {
  return `${year}-${monthValue}`;
}

function getDateRangeFromMonths(year, selectedMonths) {
  const orderedMonths = [...selectedMonths].sort();
  const firstMonth = orderedMonths[0];
  const lastMonth = orderedMonths[orderedMonths.length - 1];
  if (!firstMonth || !lastMonth) return { startDate: "", endDate: "" };

  const lastDay = new Date(Number(year), Number(lastMonth), 0).getDate();
  return {
    startDate: `${year}-${firstMonth}-01`,
    endDate: `${year}-${lastMonth}-${String(lastDay).padStart(2, "0")}`,
  };
}

function buildGroupedBarChartData(monthlyRows, selectedMonthKeys, metricRows) {
  const rowsByMonth = new Map((monthlyRows || []).map((row) => [row.monthKey, row]));

  return selectedMonthKeys.map((monthKey) => {
    const row = rowsByMonth.get(monthKey) || {};
    const month = MONTHS.find((item) => item.value === monthKey.slice(5));
    const monthLabel = month?.label || row.monthLabel || monthKey;

    return {
      name: monthLabel,
      ...Object.fromEntries(metricRows.map((metric) => [metric.key, row[metric.key] || 0])),
    };
  });
}

function buildStackedChartData(monthlyRows, selectedMonthKeys, emptyDistribution, stackOrder, scope = "global") {
  const rowsByMonth = new Map((monthlyRows || []).map((row) => [row.monthKey, row]));

  return selectedMonthKeys.map((monthKey) => {
    const row = rowsByMonth.get(monthKey) || {};
    const month = MONTHS.find((item) => item.value === monthKey.slice(5));
    const monthLabel = month?.label || row.monthLabel || monthKey;
    const distribution = Array.isArray(row[scope]) ? row[scope] : emptyDistribution;
    const distributionByName = Object.fromEntries(distribution.map((item) => [item.name, item.value || 0]));
    const categoryValues = Object.fromEntries(
      stackOrder.map((category) => [category, distributionByName[category] || 0])
    );

    return {
      name: monthLabel,
      ...categoryValues,
      total: Object.values(categoryValues).reduce((sum, value) => sum + Number(value || 0), 0),
    };
  });
}

function buildReviewMonthlyChartData(monthlyRows, selectedMonthKeys, metricRows) {
  const rowsByMonth = new Map((monthlyRows || []).map((row) => [row.monthKey, row]));

  return selectedMonthKeys.map((monthKey) => {
    const row = rowsByMonth.get(monthKey) || {};
    const month = MONTHS.find((item) => item.value === monthKey.slice(5));
    const monthLabel = month?.label || row.monthLabel || monthKey;

    return {
      name: monthLabel,
      ...Object.fromEntries(metricRows.map((metric) => [metric.key, row[metric.key] || 0])),
    };
  });
}

function SidebarIcon({ type }) {
  if (type === "patients") {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    );
  }

  if (type === "appointments") {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    );
  }

  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </svg>
  );
}

function NutriologyHeader() {
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  return (
    <header className={styles.header}>
      <div className={styles.headerText}>Deli Life Wellness</div>
      <div className={styles.headerLogoContainer}>
        <img src={logo} alt="Deli Life" className={styles.logo} />
      </div>
      <button className={styles.logoutButton} type="button" onClick={handleLogout}>
        Cerrar sesión
      </button>
    </header>
  );
}

function NutriologySidebar({ activeSection, onSectionChange }) {
  return (
    <aside className={styles.sidebar} aria-label="Navegación de nutriología">
      <nav className={styles.nav}>
        {NAV_ITEMS.map((item) => (
          <button
            key={item.key}
            className={`${styles.navItem} ${activeSection === item.key ? styles.navItemActive : ""}`}
            type="button"
            onClick={() => onSectionChange(item.key)}
          >
            <SidebarIcon type={item.icon} />
            <span>{item.label}</span>
          </button>
        ))}
      </nav>
      <div className={styles.sidebarSpacer} />
    </aside>
  );
}

export default function NutriologyDashboard() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const isPatientDetail = Boolean(patientId);
  const [activeSection, setActiveSection] = useState("general");
  const [patients, setPatients] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [analyticsYear, setAnalyticsYear] = useState(CURRENT_YEAR);
  const [selectedQuarters, setSelectedQuarters] = useState(getDefaultQuartersForYear(CURRENT_YEAR));
  const [selectedMonths, setSelectedMonths] = useState(
    getMonthsForQuarters(CURRENT_YEAR, getDefaultQuartersForYear(CURRENT_YEAR)).map((month) => month.value)
  );
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [totalsMonthly, setTotalsMonthly] = useState([]);
  const [genderMonthly, setGenderMonthly] = useState([]);
  const [analyticsJobAreas, setAnalyticsJobAreas] = useState([]);
  const [chartJobAreaFilters, setChartJobAreaFilters] = useState(EMPTY_CHART_JOB_AREA_FILTERS);
  const [reviewAnalytics, setReviewAnalytics] = useState(EMPTY_REVIEW_ANALYTICS);
  const [reviewAverageMonthly, setReviewAverageMonthly] = useState([]);
  const [reviewQuestionsMonthly, setReviewQuestionsMonthly] = useState([]);
  const [reviewAnalyticsLoading, setReviewAnalyticsLoading] = useState(false);
  const [reviewAnalyticsError, setReviewAnalyticsError] = useState("");
  const [imcEvolutionMonthly, setImcEvolutionMonthly] = useState([]);
  const [imcLoading, setImcLoading] = useState(false);
  const [bodyFatMonthly, setBodyFatMonthly] = useState([]);
  const [bodyFatLoading, setBodyFatLoading] = useState(false);
  const [waistMonthly, setWaistMonthly] = useState([]);
  const [waistLoading, setWaistLoading] = useState(false);
  const [visceralFatMonthly, setVisceralFatMonthly] = useState([]);
  const [visceralFatLoading, setVisceralFatLoading] = useState(false);
  const [diagnosisScopes, setDiagnosisScopes] = useState({
    imc: "global",
    bodyFat: "global",
    waist: "global",
    visceralFat: "global",
  });
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [patientsError, setPatientsError] = useState("");
  const [patientTableFilters, setPatientTableFilters] = useState(EMPTY_PATIENT_TABLE_FILTERS);
  const [patientTablePage, setPatientTablePage] = useState(1);
  const [isPatientModalOpen, setIsPatientModalOpen] = useState(false);
  const [patientForm, setPatientForm] = useState(EMPTY_PATIENT_FORM);
  const [savingPatient, setSavingPatient] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [appointmentsError, setAppointmentsError] = useState("");
  const [appointmentTableFilters, setAppointmentTableFilters] = useState(EMPTY_APPOINTMENT_TABLE_FILTERS);
  const [appointmentTablePage, setAppointmentTablePage] = useState(1);
  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
  const [appointmentForm, setAppointmentForm] = useState(EMPTY_APPOINTMENT_FORM);
  const [lockedAppointmentPatientId, setLockedAppointmentPatientId] = useState("");
  const [savingAppointment, setSavingAppointment] = useState(false);
  const [expandedAppointmentId, setExpandedAppointmentId] = useState("");
  const [appointmentDrafts, setAppointmentDrafts] = useState({});
  const [savingCheckUpId, setSavingCheckUpId] = useState("");
  const [copyingReviewUrlId, setCopyingReviewUrlId] = useState("");
  const [followAppointments, setFollowAppointments] = useState([]);
  const [followEligiblePatients, setFollowEligiblePatients] = useState([]);
  const [followAppointmentsLoading, setFollowAppointmentsLoading] = useState(false);
  const [followAppointmentsError, setFollowAppointmentsError] = useState("");
  const [followTableFilters, setFollowTableFilters] = useState(EMPTY_APPOINTMENT_TABLE_FILTERS);
  const [followTablePage, setFollowTablePage] = useState(1);
  const [isFollowModalOpen, setIsFollowModalOpen] = useState(false);
  const [followForm, setFollowForm] = useState(EMPTY_FOLLOW_FORM);
  const [lockedFollowPatientId, setLockedFollowPatientId] = useState("");
  const [savingFollowAppointment, setSavingFollowAppointment] = useState(false);
  const [expandedFollowAppointmentId, setExpandedFollowAppointmentId] = useState("");
  const [followDrafts, setFollowDrafts] = useState({});
  const [savingFollowCheckUpId, setSavingFollowCheckUpId] = useState("");
  const [cancellationTarget, setCancellationTarget] = useState(null);
  const [cancellationReason, setCancellationReason] = useState("");
  const [cancellationComments, setCancellationComments] = useState("");
  const [cancellationError, setCancellationError] = useState("");
  const [savingCancellation, setSavingCancellation] = useState(false);
  const [detailPatient, setDetailPatient] = useState(null);
  const [detailAppointments, setDetailAppointments] = useState([]);
  const [detailFollowAppointments, setDetailFollowAppointments] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [historicalFile, setHistoricalFile] = useState(null);
  const [historicalPreview, setHistoricalPreview] = useState(null);
  const [historicalError, setHistoricalError] = useState("");
  const [historicalErrorDetails, setHistoricalErrorDetails] = useState([]);
  const [historicalLoading, setHistoricalLoading] = useState(false);
  const [historicalSaving, setHistoricalSaving] = useState(false);
  const [historicalPreviewTab, setHistoricalPreviewTab] = useState("patients");
  const [historicalDragActive, setHistoricalDragActive] = useState(false);
  const [historicalInputKey, setHistoricalInputKey] = useState(0);
  const patientEmailIsPublic = isPublicEmail(patientForm.email);
  const section = isPatientDetail
    ? {
        title: detailPatient?.name || "Detalle del paciente",
        subtitle: "Datos generales, primeras citas y citas de seguimiento.",
      }
    : SECTION_COPY[activeSection];
  const tokenBranches = useMemo(() => getTokenBranches(), []);
  const tokenUserId = useMemo(() => getTokenUserId(), []);
  const branchNameById = useMemo(() => {
    return Object.fromEntries(branches.map((branch) => [String(branch._id), branch.name || "Sucursal"]));
  }, [branches]);
  const selectedBranch = useMemo(
    () => branches.find((branch) => String(branch._id) === String(selectedBranchId)) || null,
    [branches, selectedBranchId]
  );
  const selectedBranchJobAreas = useMemo(() => {
    return Array.isArray(selectedBranch?.jobAreas) ? selectedBranch.jobAreas.filter(Boolean) : [];
  }, [selectedBranch]);
  const detailBranch = useMemo(
    () => branches.find((branch) => String(branch._id) === String(detailPatient?.branchId)) || null,
    [branches, detailPatient]
  );
  const appointmentPatientOptions = lockedAppointmentPatientId && detailPatient
    ? [detailPatient]
    : patients;
  const followPatientOptions = lockedFollowPatientId && detailPatient
    ? [detailPatient]
    : followEligiblePatients;
  const selectedAppointmentPatient = appointmentPatientOptions.find(
    (patient) => String(patient._id) === String(appointmentForm.patientId)
  );
  const appointmentIsNewPatient = appointmentForm.patientMode === "new";
  const appointmentNewPatient = appointmentForm.newPatient || EMPTY_PATIENT_FORM;
  const appointmentNewPatientEmailIsPublic = isPublicEmail(appointmentNewPatient.email);
  const appointmentNewPatientBranch = branches.find(
    (branch) => String(branch._id) === String(appointmentNewPatient.branchId)
  );
  const appointmentNewPatientJobAreas = Array.isArray(appointmentNewPatientBranch?.jobAreas)
    ? appointmentNewPatientBranch.jobAreas.filter(Boolean)
    : [];
  const selectedFollowPatient = followPatientOptions.find(
    (patient) => String(patient._id) === String(followForm.patientId)
  );
  const selectedAppointmentBranchName =
    appointmentIsNewPatient
      ? branchNameById[String(appointmentNewPatient.branchId)] || "Selecciona sucursal"
      : branchNameById[String(selectedAppointmentPatient?.branchId)] || "Se obtiene del paciente";
  const selectedFollowBranchName =
    branchNameById[String(selectedFollowPatient?.branchId)] || "Se obtiene del paciente";
  const patientJobAreaOptions = useMemo(
    () => getUniqueOptions([...selectedBranchJobAreas, ...patients.map((patient) => patient.jobArea)]),
    [patients, selectedBranchJobAreas]
  );
  const appointmentJobAreaOptions = useMemo(
    () => getUniqueOptions([...selectedBranchJobAreas, ...appointments.map((appointment) => appointment.patientJobArea)]),
    [appointments, selectedBranchJobAreas]
  );
  const followJobAreaOptions = useMemo(
    () => getUniqueOptions([...selectedBranchJobAreas, ...followAppointments.map((appointment) => appointment.patientJobArea)]),
    [followAppointments, selectedBranchJobAreas]
  );
  const filteredPatients = useMemo(() => {
    const nameSearch = normalizeFilterText(patientTableFilters.name);
    const emailSearch = normalizeFilterText(patientTableFilters.email);
    return patients.filter((patient) => (
      (!nameSearch || normalizeFilterText(patient.name).includes(nameSearch)) &&
      (!emailSearch || normalizeFilterText(patient.email).includes(emailSearch)) &&
      (!patientTableFilters.gender || patient.gender === patientTableFilters.gender) &&
      (!patientTableFilters.jobArea || patient.jobArea === patientTableFilters.jobArea)
    ));
  }, [patientTableFilters, patients]);
  const filteredAppointments = useMemo(
    () => filterAppointmentRows(appointments, appointmentTableFilters),
    [appointmentTableFilters, appointments]
  );
  const filteredFollowAppointments = useMemo(
    () => filterAppointmentRows(followAppointments, followTableFilters, { followUp: true }),
    [followAppointments, followTableFilters]
  );
  const patientPageCount = Math.max(1, Math.ceil(filteredPatients.length / TABLE_PAGE_SIZE));
  const appointmentPageCount = Math.max(1, Math.ceil(filteredAppointments.length / TABLE_PAGE_SIZE));
  const followPageCount = Math.max(1, Math.ceil(filteredFollowAppointments.length / TABLE_PAGE_SIZE));
  const safePatientPage = Math.min(patientTablePage, patientPageCount);
  const safeAppointmentPage = Math.min(appointmentTablePage, appointmentPageCount);
  const safeFollowPage = Math.min(followTablePage, followPageCount);
  const paginatedPatients = filteredPatients.slice(
    (safePatientPage - 1) * TABLE_PAGE_SIZE,
    safePatientPage * TABLE_PAGE_SIZE
  );
  const paginatedAppointments = filteredAppointments.slice(
    (safeAppointmentPage - 1) * TABLE_PAGE_SIZE,
    safeAppointmentPage * TABLE_PAGE_SIZE
  );
  const paginatedFollowAppointments = filteredFollowAppointments.slice(
    (safeFollowPage - 1) * TABLE_PAGE_SIZE,
    safeFollowPage * TABLE_PAGE_SIZE
  );
  const availableYears = useMemo(() => getAvailableYears(), []);
  const availableQuarters = useMemo(() => getAvailableQuarters(analyticsYear), [analyticsYear]);
  const availableMonths = useMemo(() => getMonthsForQuarters(analyticsYear, selectedQuarters), [analyticsYear, selectedQuarters]);
  const selectedMonthKeys = useMemo(
    () => selectedMonths.map((month) => getMonthKey(analyticsYear, month)).sort(),
    [analyticsYear, selectedMonths]
  );
  const analyticsDateRange = useMemo(
    () => getDateRangeFromMonths(analyticsYear, selectedMonths),
    [analyticsYear, selectedMonths]
  );
  const visibleTotalsRows = useMemo(
    () => totalsMonthly.filter((row) => selectedMonthKeys.includes(row.monthKey)),
    [selectedMonthKeys, totalsMonthly]
  );
  const visibleGenderRows = useMemo(
    () => genderMonthly.filter((row) => selectedMonthKeys.includes(row.monthKey)),
    [genderMonthly, selectedMonthKeys]
  );
  const totalsChartData = useMemo(
    () =>
      buildGroupedBarChartData(visibleTotalsRows, selectedMonthKeys, TOTALS_BAR_SERIES),
    [selectedMonthKeys, visibleTotalsRows]
  );
  const genderChartData = useMemo(
    () =>
      buildGroupedBarChartData(visibleGenderRows, selectedMonthKeys, GENDER_BAR_SERIES),
    [selectedMonthKeys, visibleGenderRows]
  );
  const imcEvolutionStackedChartData = useMemo(
    () => buildStackedChartData(imcEvolutionMonthly, selectedMonthKeys, EMPTY_IMC_DISTRIBUTION, IMC_EVOLUTION_LINE_ORDER, diagnosisScopes.imc),
    [diagnosisScopes.imc, imcEvolutionMonthly, selectedMonthKeys]
  );
  const bodyFatChartData = useMemo(
    () => buildStackedChartData(bodyFatMonthly, selectedMonthKeys, EMPTY_BODY_FAT_DISTRIBUTION, BODY_FAT_STACK_ORDER, diagnosisScopes.bodyFat),
    [bodyFatMonthly, diagnosisScopes.bodyFat, selectedMonthKeys]
  );
  const waistChartData = useMemo(
    () => buildStackedChartData(waistMonthly, selectedMonthKeys, EMPTY_WAIST_DISTRIBUTION, WAIST_STACK_ORDER, diagnosisScopes.waist),
    [diagnosisScopes.waist, selectedMonthKeys, waistMonthly]
  );
  const visceralFatChartData = useMemo(
    () => buildStackedChartData(visceralFatMonthly, selectedMonthKeys, EMPTY_VISCERAL_FAT_DISTRIBUTION, VISCERAL_FAT_STACK_ORDER, diagnosisScopes.visceralFat),
    [diagnosisScopes.visceralFat, selectedMonthKeys, visceralFatMonthly]
  );
  const reviewQuestionMonthlyChartData = useMemo(
    () => buildReviewMonthlyChartData(reviewQuestionsMonthly, selectedMonthKeys, REVIEW_QUESTION_SERIES),
    [reviewQuestionsMonthly, selectedMonthKeys]
  );
  const reviewAverageMonthlyChartData = useMemo(
    () => buildReviewMonthlyChartData(reviewAverageMonthly, selectedMonthKeys, REVIEW_AVERAGE_SERIES),
    [reviewAverageMonthly, selectedMonthKeys]
  );

  const handleSectionChange = (sectionKey) => {
    setActiveSection(sectionKey);
    if (isPatientDetail) {
      navigate("/nutrilogyPanel");
    }
  };

  const handlePatientTableFilterChange = (field, value) => {
    setPatientTableFilters((prev) => ({ ...prev, [field]: value }));
    setPatientTablePage(1);
  };

  const handleAppointmentTableFilterChange = (field, value) => {
    setAppointmentTableFilters((prev) => ({ ...prev, [field]: value }));
    setAppointmentTablePage(1);
    setExpandedAppointmentId("");
  };

  const handleFollowTableFilterChange = (field, value) => {
    setFollowTableFilters((prev) => ({ ...prev, [field]: value }));
    setFollowTablePage(1);
    setExpandedFollowAppointmentId("");
  };

  const renderTablePagination = ({ currentPage, pageCount, totalItems, onPageChange }) => {
    if (!totalItems) return null;
    const firstItem = ((currentPage - 1) * TABLE_PAGE_SIZE) + 1;
    const lastItem = Math.min(currentPage * TABLE_PAGE_SIZE, totalItems);

    return (
      <div className={styles.tablePagination}>
        <span>Mostrando {firstItem}–{lastItem} de {totalItems}</span>
        <div className={styles.paginationControls}>
          <button
            type="button"
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          >
            Anterior
          </button>
          <strong>Página {currentPage} de {pageCount}</strong>
          <button
            type="button"
            onClick={() => onPageChange(Math.min(pageCount, currentPage + 1))}
            disabled={currentPage === pageCount}
          >
            Siguiente
          </button>
        </div>
      </div>
    );
  };

  const goBackToPatients = () => {
    setActiveSection("patients");
    navigate("/nutrilogyPanel");
  };

  const handleAnalyticsYearChange = (year) => {
    const nextYear = Number(year);
    const nextQuarters = getDefaultQuartersForYear(nextYear);
    setAnalyticsYear(nextYear);
    setSelectedQuarters(nextQuarters);
    setSelectedMonths(getMonthsForQuarters(nextYear, nextQuarters).map((month) => month.value));
  };

  const handleQuarterToggle = (quarterValue) => {
    setSelectedQuarters((prev) => {
      const hasQuarter = prev.includes(quarterValue);
      const nextQuarters = hasQuarter
        ? prev.filter((quarter) => quarter !== quarterValue)
        : [...prev, quarterValue].sort((a, b) => a - b);
      const safeQuarters = nextQuarters.length ? nextQuarters : [quarterValue];
      const availableMonthValues = getMonthsForQuarters(analyticsYear, safeQuarters).map((month) => month.value);
      setSelectedMonths((currentMonths) => {
        const nextMonths = currentMonths.filter((month) => availableMonthValues.includes(month));
        return nextMonths.length ? nextMonths : availableMonthValues;
      });
      return safeQuarters;
    });
  };

  const handleMonthToggle = (monthValue) => {
    setSelectedMonths((prev) => {
      const nextMonths = prev.includes(monthValue)
        ? prev.filter((month) => month !== monthValue)
        : [...prev, monthValue].sort();
      return nextMonths.length ? nextMonths : prev;
    });
  };

  const handleDiagnosisScopeChange = (chartKey, scope) => {
    setDiagnosisScopes((prev) => ({ ...prev, [chartKey]: scope }));
  };

  const handleChartJobAreaChange = (chartKey, jobArea) => {
    setChartJobAreaFilters((prev) => ({ ...prev, [chartKey]: jobArea }));
  };

  const resetHistoricalUpload = () => {
    setHistoricalFile(null);
    setHistoricalPreview(null);
    setHistoricalError("");
    setHistoricalErrorDetails([]);
    setHistoricalLoading(false);
    setHistoricalSaving(false);
    setHistoricalPreviewTab("patients");
    setHistoricalDragActive(false);
    setHistoricalInputKey((key) => key + 1);
  };

  const setHistoricalUploadFile = (file) => {
    setHistoricalError("");
    setHistoricalErrorDetails([]);
    setHistoricalPreview(null);
    if (!file) {
      setHistoricalFile(null);
      return;
    }
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      setHistoricalFile(null);
      setHistoricalInputKey((key) => key + 1);
      setHistoricalError("Solo se permiten archivos .xlsx.");
      setHistoricalErrorDetails([]);
      return;
    }
    setHistoricalFile(file);
  };

  const handleHistoricalFileInput = (event) => {
    setHistoricalUploadFile(event.target.files?.[0] || null);
  };

  const handleHistoricalDrop = (event) => {
    event.preventDefault();
    setHistoricalDragActive(false);
    setHistoricalUploadFile(event.dataTransfer.files?.[0] || null);
  };

  const handleHistoricalPreview = async () => {
    if (!selectedBranchId) {
      setHistoricalError("Selecciona una sucursal antes de cargar historico.");
      setHistoricalErrorDetails([]);
      return;
    }
    if (!historicalFile) {
      setHistoricalError("Selecciona un archivo .xlsx.");
      setHistoricalErrorDetails([]);
      return;
    }

    setHistoricalLoading(true);
    setHistoricalError("");
    setHistoricalErrorDetails([]);
    try {
      const result = await patientsApi.uploadWorkbook({
        branchId: selectedBranchId,
        file: historicalFile,
        commit: false,
      });
      setHistoricalPreview(result);
      setHistoricalPreviewTab("patients");
    } catch (error) {
      setHistoricalPreview(null);
      setHistoricalError(error.response?.data?.message || "No se pudo leer el historico.");
      setHistoricalErrorDetails(Array.isArray(error.response?.data?.errors) ? error.response.data.errors : []);
    } finally {
      setHistoricalLoading(false);
    }
  };

  const handleHistoricalCommit = async () => {
    if (!selectedBranchId || !historicalFile || !historicalPreview?.summary?.xlsxID) return;

    setHistoricalSaving(true);
    setHistoricalError("");
    setHistoricalErrorDetails([]);
    try {
      await patientsApi.uploadWorkbook({
        branchId: selectedBranchId,
        file: historicalFile,
        commit: true,
        xlsxID: historicalPreview.summary.xlsxID,
      });
      resetHistoricalUpload();
      await Promise.all([
        loadPatients(),
        loadSummary(),
        loadImcDistribution(),
        loadBodyFatDistribution(),
        loadWaistDistribution(),
        loadVisceralFatDistribution(),
      ]);
    } catch (error) {
      setHistoricalError(error.response?.data?.message || "No se pudo guardar el historico.");
      setHistoricalErrorDetails(Array.isArray(error.response?.data?.errors) ? error.response.data.errors : []);
      setHistoricalSaving(false);
    }
  };

  const loadBranches = useCallback(async () => {
    if (!tokenBranches.length) return;
    try {
      const loadedBranches = await Promise.all(
        tokenBranches.map(async (branchId) => {
          try {
            return await companyBranchesApi.getById(branchId);
          } catch (_) {
            return { _id: branchId, name: "Sucursal" };
          }
        })
      );
      setBranches(loadedBranches);
      if (loadedBranches.length === 1) {
        setSelectedBranchId(String(loadedBranches[0]._id));
        setPatientForm((prev) => ({ ...prev, branchId: String(loadedBranches[0]._id) }));
      }
    } catch (_) {
      setBranches(tokenBranches.map((branchId) => ({ _id: branchId, name: "Sucursal" })));
    }
  }, [tokenBranches]);

  const loadPatients = useCallback(async () => {
    setPatientsLoading(true);
    setPatientsError("");
    try {
      const loadedPatients = await patientsApi.getAll({ branchId: selectedBranchId || undefined });
      setPatients(Array.isArray(loadedPatients) ? loadedPatients : []);
    } catch (error) {
      setPatients([]);
      setPatientsError(error.response?.data?.message || "No se pudieron cargar los pacientes.");
    } finally {
      setPatientsLoading(false);
    }
  }, [selectedBranchId]);

  const loadAnalyticsJobAreas = useCallback(async () => {
    try {
      const loadedJobAreas = await patientsApi.getAnalyticsJobAreas({
        branchId: selectedBranchId || undefined,
        startDate: analyticsDateRange.startDate,
        endDate: analyticsDateRange.endDate,
      });
      const safeJobAreas = Array.isArray(loadedJobAreas) ? loadedJobAreas.filter(Boolean) : [];
      setAnalyticsJobAreas(safeJobAreas);
      setChartJobAreaFilters((prev) =>
        Object.fromEntries(
          Object.entries(prev).map(([chartKey, jobArea]) => [
            chartKey,
            !jobArea || safeJobAreas.includes(jobArea) ? jobArea : "",
          ])
        )
      );
    } catch (_) {
      setAnalyticsJobAreas([]);
      setChartJobAreaFilters(EMPTY_CHART_JOB_AREA_FILTERS);
    }
  }, [analyticsDateRange.endDate, analyticsDateRange.startDate, selectedBranchId]);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const requestSummary = (jobArea) => patientsApi.getSummary({
        branchId: selectedBranchId || undefined,
        startDate: analyticsDateRange.startDate,
        endDate: analyticsDateRange.endDate,
        jobArea: jobArea || undefined,
      });
      const [loadedSummary, loadedTotalsSummary, loadedGenderSummary] = await Promise.all([
        requestSummary(""),
        requestSummary(chartJobAreaFilters.totals),
        requestSummary(chartJobAreaFilters.gender),
      ]);
      setSummary({
        patientsCount: loadedSummary?.patientsCount || 0,
        malePatientsCount: loadedSummary?.malePatientsCount || 0,
        femalePatientsCount: loadedSummary?.femalePatientsCount || 0,
        firstCheckUpsCount: loadedSummary?.firstCheckUpsCount || 0,
        followCheckUpsCount: loadedSummary?.followCheckUpsCount || 0,
        monthly: Array.isArray(loadedSummary?.monthly) ? loadedSummary.monthly : [],
      });
      setTotalsMonthly(Array.isArray(loadedTotalsSummary?.monthly) ? loadedTotalsSummary.monthly : []);
      setGenderMonthly(Array.isArray(loadedGenderSummary?.monthly) ? loadedGenderSummary.monthly : []);
    } catch (_) {
      setSummary(EMPTY_SUMMARY);
      setTotalsMonthly([]);
      setGenderMonthly([]);
    } finally {
      setSummaryLoading(false);
    }
  }, [
    analyticsDateRange.endDate,
    analyticsDateRange.startDate,
    chartJobAreaFilters.gender,
    chartJobAreaFilters.totals,
    selectedBranchId,
  ]);

  const loadReviewAnalytics = useCallback(async () => {
    setReviewAnalyticsLoading(true);
    setReviewAnalyticsError("");
    try {
      const requestAnalytics = (jobArea) => checkUpReviewsApi.getAnalytics({
        branchId: selectedBranchId || undefined,
        nutriologyId: tokenUserId || undefined,
        startDate: analyticsDateRange.startDate,
        endDate: analyticsDateRange.endDate,
        jobArea: jobArea || undefined,
      });
      const [loadedAnalytics, loadedAverageAnalytics, loadedQuestionsAnalytics] = await Promise.all([
        requestAnalytics(""),
        requestAnalytics(chartJobAreaFilters.reviewAverage),
        requestAnalytics(chartJobAreaFilters.reviewQuestions),
      ]);
      setReviewAnalytics({
        count: loadedAnalytics?.count || 0,
        questions: Array.isArray(loadedAnalytics?.questions) ? loadedAnalytics.questions : [],
        monthly: Array.isArray(loadedAnalytics?.monthly) ? loadedAnalytics.monthly : [],
        comments: Array.isArray(loadedAnalytics?.comments) ? loadedAnalytics.comments : [],
      });
      setReviewAverageMonthly(
        Array.isArray(loadedAverageAnalytics?.monthly) ? loadedAverageAnalytics.monthly : []
      );
      setReviewQuestionsMonthly(
        Array.isArray(loadedQuestionsAnalytics?.monthly) ? loadedQuestionsAnalytics.monthly : []
      );
    } catch (error) {
      setReviewAnalytics(EMPTY_REVIEW_ANALYTICS);
      setReviewAverageMonthly([]);
      setReviewQuestionsMonthly([]);
      setReviewAnalyticsError(error.response?.data?.message || "No se pudieron cargar las evaluaciones.");
    } finally {
      setReviewAnalyticsLoading(false);
    }
  }, [
    analyticsDateRange.endDate,
    analyticsDateRange.startDate,
    chartJobAreaFilters.reviewAverage,
    chartJobAreaFilters.reviewQuestions,
    selectedBranchId,
    tokenUserId,
  ]);

  const loadImcDistribution = useCallback(async () => {
    setImcLoading(true);
    try {
      const loadedDistribution = await patientsApi.getImcDistribution({
        branchId: selectedBranchId || undefined,
        startDate: analyticsDateRange.startDate,
        endDate: analyticsDateRange.endDate,
        jobArea: chartJobAreaFilters.imc || undefined,
      });
      setImcEvolutionMonthly(Array.isArray(loadedDistribution?.evolutionMonthly) ? loadedDistribution.evolutionMonthly : []);
    } catch (_) {
      setImcEvolutionMonthly([]);
    } finally {
      setImcLoading(false);
    }
  }, [analyticsDateRange.endDate, analyticsDateRange.startDate, chartJobAreaFilters.imc, selectedBranchId]);

  const loadBodyFatDistribution = useCallback(async () => {
    setBodyFatLoading(true);
    try {
      const loadedDistribution = await patientsApi.getBodyFatDistribution({
        branchId: selectedBranchId || undefined,
        startDate: analyticsDateRange.startDate,
        endDate: analyticsDateRange.endDate,
        jobArea: chartJobAreaFilters.bodyFat || undefined,
      });
      setBodyFatMonthly(Array.isArray(loadedDistribution?.monthly) ? loadedDistribution.monthly : []);
    } catch (_) {
      setBodyFatMonthly([]);
    } finally {
      setBodyFatLoading(false);
    }
  }, [analyticsDateRange.endDate, analyticsDateRange.startDate, chartJobAreaFilters.bodyFat, selectedBranchId]);

  const loadWaistDistribution = useCallback(async () => {
    setWaistLoading(true);
    try {
      const loadedDistribution = await patientsApi.getWaistDistribution({
        branchId: selectedBranchId || undefined,
        startDate: analyticsDateRange.startDate,
        endDate: analyticsDateRange.endDate,
        jobArea: chartJobAreaFilters.waist || undefined,
      });
      setWaistMonthly(Array.isArray(loadedDistribution?.monthly) ? loadedDistribution.monthly : []);
    } catch (_) {
      setWaistMonthly([]);
    } finally {
      setWaistLoading(false);
    }
  }, [analyticsDateRange.endDate, analyticsDateRange.startDate, chartJobAreaFilters.waist, selectedBranchId]);

  const loadVisceralFatDistribution = useCallback(async () => {
    setVisceralFatLoading(true);
    try {
      const loadedDistribution = await patientsApi.getVisceralFatDistribution({
        branchId: selectedBranchId || undefined,
        startDate: analyticsDateRange.startDate,
        endDate: analyticsDateRange.endDate,
        jobArea: chartJobAreaFilters.visceralFat || undefined,
      });
      setVisceralFatMonthly(Array.isArray(loadedDistribution?.monthly) ? loadedDistribution.monthly : []);
    } catch (_) {
      setVisceralFatMonthly([]);
    } finally {
      setVisceralFatLoading(false);
    }
  }, [analyticsDateRange.endDate, analyticsDateRange.startDate, chartJobAreaFilters.visceralFat, selectedBranchId]);

  const loadAppointments = useCallback(async () => {
    setAppointmentsLoading(true);
    setAppointmentsError("");
    try {
      const loadedPatients = await patientsApi.getAll({ branchId: selectedBranchId || undefined });
      const safePatients = Array.isArray(loadedPatients) ? loadedPatients : [];
      const rowsByPatient = await Promise.all(
        safePatients.map(async (patient) => {
          const checkups = await patientsApi.getCheckUps(patient._id);
          return (Array.isArray(checkups) ? checkups : [])
            .map((checkup) => ({
              ...checkup,
              patientId: patient._id,
              patientName: patient.name,
              patientEmail: patient.email,
              patientJobArea: patient.jobArea,
              patientGender: patient.gender,
              patientAge: patient.age,
            }));
        })
      );

      setPatients(safePatients);
      setAppointments(rowsByPatient.flat().sort((a, b) => new Date(b.date) - new Date(a.date)));
      setExpandedAppointmentId("");
      setAppointmentDrafts({});
    } catch (error) {
      setAppointments([]);
      setAppointmentsError(error.response?.data?.message || "No se pudieron cargar las citas.");
    } finally {
      setAppointmentsLoading(false);
    }
  }, [selectedBranchId]);

  const loadFollowAppointments = useCallback(async () => {
    setFollowAppointmentsLoading(true);
    setFollowAppointmentsError("");
    try {
      const loadedPatients = await patientsApi.getAll({ branchId: selectedBranchId || undefined });
      const safePatients = Array.isArray(loadedPatients) ? loadedPatients : [];
      const patientsWithFirstCheckUpRows = await Promise.all(
        safePatients.map(async (patient) => {
          const checkups = await patientsApi.getCheckUps(patient._id);
          return {
            patient,
            hasFirstCheckUp:
              Array.isArray(checkups) && checkups.some((checkup) => !isAppointmentCancelled(checkup)),
          };
        })
      );
      const eligiblePatients = patientsWithFirstCheckUpRows
        .filter((row) => row.hasFirstCheckUp)
        .map((row) => row.patient);
      const patientById = Object.fromEntries(safePatients.map((patient) => [String(patient._id), patient]));
      const loadedFollowAppointments = await followCheckUpsApi.getAll({ branchId: selectedBranchId || undefined });
      const rows = (Array.isArray(loadedFollowAppointments) ? loadedFollowAppointments : []).map((followCheckUp) => {
        const patient = patientById[String(followCheckUp.patientId)] || {};
        return {
          ...followCheckUp,
          patientId: followCheckUp.patientId,
          patientName: patient.name || "Paciente",
          patientEmail: patient.email,
          patientJobArea: patient.jobArea,
        };
      });

      setPatients(safePatients);
      setFollowEligiblePatients(eligiblePatients);
      setFollowAppointments(rows.sort((a, b) => new Date(b.date) - new Date(a.date)));
      setExpandedFollowAppointmentId("");
      setFollowDrafts({});
    } catch (error) {
      setFollowAppointments([]);
      setFollowEligiblePatients([]);
      setFollowAppointmentsError(error.response?.data?.message || "No se pudieron cargar las citas de seguimiento.");
    } finally {
      setFollowAppointmentsLoading(false);
    }
  }, [selectedBranchId]);

  const loadPatientDetail = useCallback(async () => {
    if (!patientId) return;

    setDetailLoading(true);
    setDetailError("");
    try {
      const [patient, checkups, followCheckups] = await Promise.all([
        patientsApi.getById(patientId),
        patientsApi.getCheckUps(patientId),
        followCheckUpsApi.getAll({ patientId }),
      ]);
      const safeCheckups = Array.isArray(checkups) ? checkups : [];
      const safeFollowCheckups = Array.isArray(followCheckups) ? followCheckups : [];
      setDetailPatient(patient);
      if (patient?.branchId) {
        setSelectedBranchId(String(patient.branchId));
      }
      setDetailAppointments(
        safeCheckups
          .map((checkup) => ({
            ...checkup,
            patientId: patient._id,
            patientName: patient.name,
            patientEmail: patient.email,
            patientJobArea: patient.jobArea,
            patientGender: patient.gender,
            patientAge: patient.age,
          }))
          .sort((a, b) => new Date(b.date) - new Date(a.date))
      );
      setDetailFollowAppointments(
        safeFollowCheckups
          .map((followCheckUp) => ({
            ...followCheckUp,
            patientId: patient._id,
            patientName: patient.name,
            patientEmail: patient.email,
            patientJobArea: patient.jobArea,
          }))
          .sort((a, b) => new Date(b.date) - new Date(a.date))
      );
    } catch (error) {
      setDetailPatient(null);
      setDetailAppointments([]);
      setDetailFollowAppointments([]);
      setDetailError(error.response?.data?.message || "No se pudo cargar el detalle del paciente.");
    } finally {
      setDetailLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  useEffect(() => {
    setPatientTableFilters(EMPTY_PATIENT_TABLE_FILTERS);
    setAppointmentTableFilters(EMPTY_APPOINTMENT_TABLE_FILTERS);
    setFollowTableFilters(EMPTY_APPOINTMENT_TABLE_FILTERS);
    setPatientTablePage(1);
    setAppointmentTablePage(1);
    setFollowTablePage(1);
    setExpandedAppointmentId("");
    setExpandedFollowAppointmentId("");
  }, [selectedBranchId]);

  useEffect(() => {
    if (isPatientDetail) return;
    if (activeSection === "patients") loadPatients();
    if (activeSection === "appointments") loadAppointments();
    if (activeSection === "followAppointments") loadFollowAppointments();
  }, [activeSection, isPatientDetail, loadAppointments, loadFollowAppointments, loadPatients]);

  useEffect(() => {
    if (isPatientDetail) loadPatientDetail();
  }, [isPatientDetail, loadPatientDetail]);

  useEffect(() => {
    if (!isPatientDetail) loadSummary();
  }, [isPatientDetail, loadSummary]);

  useEffect(() => {
    if (!isPatientDetail) loadAnalyticsJobAreas();
  }, [isPatientDetail, loadAnalyticsJobAreas]);

  useEffect(() => {
    if (!isPatientDetail && activeSection === "generalEvaluation") loadReviewAnalytics();
  }, [activeSection, isPatientDetail, loadReviewAnalytics]);

  useEffect(() => {
    if (!isPatientDetail) loadImcDistribution();
  }, [isPatientDetail, loadImcDistribution]);

  useEffect(() => {
    if (!isPatientDetail) loadBodyFatDistribution();
  }, [isPatientDetail, loadBodyFatDistribution]);

  useEffect(() => {
    if (!isPatientDetail) loadWaistDistribution();
  }, [isPatientDetail, loadWaistDistribution]);

  useEffect(() => {
    if (!isPatientDetail) loadVisceralFatDistribution();
  }, [isPatientDetail, loadVisceralFatDistribution]);

  const openPatientModal = () => {
    if (!selectedBranchId) {
      setPatientsError("Selecciona una sucursal antes de agregar un paciente.");
      return;
    }
    setPatientsError("");
    setPatientForm({
      ...EMPTY_PATIENT_FORM,
      branchId: selectedBranchId,
    });
    setIsPatientModalOpen(true);
  };

  const closePatientModal = () => {
    setIsPatientModalOpen(false);
    setPatientForm(EMPTY_PATIENT_FORM);
    setSavingPatient(false);
  };

  const handlePatientFormChange = (field, value) => {
    setPatientForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAppointmentFormChange = (field, value) => {
    setAppointmentForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAppointmentPatientModeChange = (mode) => {
    setAppointmentForm((prev) => ({
      ...prev,
      patientMode: mode,
      patientId: mode === "new" ? "" : prev.patientId,
      newPatient: {
        ...EMPTY_PATIENT_FORM,
        branchId: selectedBranchId || "",
      },
    }));
  };

  const handleAppointmentNewPatientChange = (field, value) => {
    setAppointmentForm((prev) => ({
      ...prev,
      newPatient: {
        ...(prev.newPatient || EMPTY_PATIENT_FORM),
        [field]: value,
        ...(field === "branchId" ? { jobArea: "" } : {}),
      },
    }));
  };

  const handleMultiOptionToggle = (field, option) => {
    setAppointmentForm((prev) => {
      const selectedOptions = splitMultiValue(prev[field]);
      const nextOptions = selectedOptions.includes(option)
        ? selectedOptions.filter((item) => item !== option)
        : [...selectedOptions, option];

      return { ...prev, [field]: nextOptions.join(", ") };
    });
  };

  const toggleAppointmentDetails = (appointment) => {
    setExpandedAppointmentId((currentId) => (currentId === appointment._id ? "" : appointment._id));
    setAppointmentDrafts((prev) => ({
      ...prev,
      [appointment._id]: prev[appointment._id] || getCheckUpDraft(appointment),
    }));
  };

  const handleAppointmentDraftChange = (appointmentId, field, value) => {
    setAppointmentDrafts((prev) => ({
      ...prev,
      [appointmentId]: {
        ...(prev[appointmentId] || {}),
        [field]: value,
      },
    }));
  };

  const handleAppointmentDraftOptionToggle = (appointmentId, field, option) => {
    setAppointmentDrafts((prev) => {
      const draft = prev[appointmentId] || {};
      const selectedOptions = splitMultiValue(draft[field]);
      const nextOptions = selectedOptions.includes(option)
        ? selectedOptions.filter((item) => item !== option)
        : [...selectedOptions, option];

      return {
        ...prev,
        [appointmentId]: {
          ...draft,
          [field]: nextOptions.join(", "),
        },
      };
    });
  };

  const handleUpdateAppointmentCheckUp = async (appointment) => {
    const draft = appointmentDrafts[appointment._id] || getCheckUpDraft(appointment);
    setSavingCheckUpId(appointment._id);
    setAppointmentsError("");

    try {
      const updated = await patientsApi.updateCheckUp(
        appointment.patientId,
        appointment._id,
        getCheckUpPayloadFromDraft(draft)
      );

      setAppointments((prev) =>
        prev
          .map((item) =>
            item._id === appointment._id
              ? {
                  ...item,
                  ...updated,
                  patientId: item.patientId,
                  patientName: item.patientName,
                  patientEmail: item.patientEmail,
                  patientJobArea: item.patientJobArea,
                }
              : item
          )
          .sort((a, b) => new Date(b.date) - new Date(a.date))
      );
      setDetailAppointments((prev) =>
        prev
          .map((item) =>
            item._id === appointment._id
              ? {
                  ...item,
                  ...updated,
                  patientId: item.patientId,
                  patientName: item.patientName,
                  patientEmail: item.patientEmail,
                  patientJobArea: item.patientJobArea,
                }
              : item
          )
          .sort((a, b) => new Date(b.date) - new Date(a.date))
      );
      setAppointmentDrafts((prev) => ({
        ...prev,
        [appointment._id]: getCheckUpDraft(updated),
      }));
      await Promise.all([
        loadImcDistribution(),
        loadBodyFatDistribution(),
        loadWaistDistribution(),
        loadVisceralFatDistribution(),
      ]);
    } catch (error) {
      setAppointmentsError(error.response?.data?.message || "No se pudo actualizar la cita.");
    } finally {
      setSavingCheckUpId("");
    }
  };

  const copyTextToClipboard = async (text) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  };

  const handleCopyReviewUrl = async (appointment, appointmentType) => {
    setCopyingReviewUrlId(appointment._id);
    const setError = appointmentType === "checkup" ? setAppointmentsError : setFollowAppointmentsError;
    setError("");

    try {
      const { reviewUrl } = await checkUpReviewsApi.getReviewUrl({
        appointmentType,
        appointmentId: appointment._id,
      });
      if (!reviewUrl) throw new Error("URL no disponible");
      await copyTextToClipboard(reviewUrl);
    } catch (error) {
      setError(error.response?.data?.message || "No se pudo copiar la URL de review.");
    } finally {
      setCopyingReviewUrlId("");
    }
  };

  const openFollowModal = (patientIdOverride = "") => {
    const hasEligiblePatient = patientIdOverride || followEligiblePatients.length;
    if (!hasEligiblePatient) {
      setFollowAppointmentsError("No hay pacientes con primera cita registrada para crear seguimiento.");
      return;
    }
    setFollowAppointmentsError("");
    setLockedFollowPatientId(patientIdOverride);
    setFollowForm({
      ...EMPTY_FOLLOW_FORM,
      patientId: patientIdOverride,
    });
    setIsFollowModalOpen(true);
  };

  const closeFollowModal = () => {
    setIsFollowModalOpen(false);
    setFollowForm(EMPTY_FOLLOW_FORM);
    setLockedFollowPatientId("");
    setSavingFollowAppointment(false);
  };

  const handleFollowFormChange = (field, value) => {
    setFollowForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleFollowAppointmentDetails = (followAppointment) => {
    setExpandedFollowAppointmentId((currentId) =>
      currentId === followAppointment._id ? "" : followAppointment._id
    );
    setFollowDrafts((prev) => ({
      ...prev,
      [followAppointment._id]: prev[followAppointment._id] || getFollowDraft(followAppointment),
    }));
  };

  const handleFollowDraftChange = (followAppointmentId, field, value) => {
    setFollowDrafts((prev) => ({
      ...prev,
      [followAppointmentId]: {
        ...(prev[followAppointmentId] || {}),
        [field]: value,
      },
    }));
  };

  const handleCreateFollowAppointment = async (event) => {
    event.preventDefault();
    setSavingFollowAppointment(true);
    setFollowAppointmentsError("");

    try {
      await followCheckUpsApi.create(getFollowPayloadFromDraft(followForm, followForm.patientId));
      closeFollowModal();
      if (isPatientDetail) {
        await loadPatientDetail();
      } else {
        await loadFollowAppointments();
      }
      await loadSummary();
    } catch (error) {
      setFollowAppointmentsError(error.response?.data?.message || "No se pudo crear la cita de seguimiento.");
      setSavingFollowAppointment(false);
    }
  };

  const handleUpdateFollowAppointment = async (followAppointment) => {
    const draft = followDrafts[followAppointment._id] || getFollowDraft(followAppointment);
    setSavingFollowCheckUpId(followAppointment._id);
    setFollowAppointmentsError("");

    try {
      const updated = await followCheckUpsApi.update(
        followAppointment._id,
        getFollowPayloadFromDraft(draft, followAppointment.patientId)
      );
      setFollowAppointments((prev) =>
        prev
          .map((item) =>
            item._id === followAppointment._id
              ? {
                  ...item,
                  ...updated,
                  patientId: item.patientId,
                  patientName: item.patientName,
                  patientEmail: item.patientEmail,
                  patientJobArea: item.patientJobArea,
                }
              : item
          )
          .sort((a, b) => new Date(b.date) - new Date(a.date))
      );
      setDetailFollowAppointments((prev) =>
        prev
          .map((item) =>
            item._id === followAppointment._id
              ? {
                  ...item,
                  ...updated,
                  patientId: item.patientId,
                  patientName: item.patientName,
                  patientEmail: item.patientEmail,
                  patientJobArea: item.patientJobArea,
                }
              : item
          )
          .sort((a, b) => new Date(b.date) - new Date(a.date))
      );
      setFollowDrafts((prev) => ({
        ...prev,
        [followAppointment._id]: getFollowDraft(updated),
      }));
    } catch (error) {
      setFollowAppointmentsError(error.response?.data?.message || "No se pudo actualizar la cita de seguimiento.");
    } finally {
      setSavingFollowCheckUpId("");
    }
  };

  const openCancellationModal = (appointment, appointmentType) => {
    setCancellationTarget({ appointment, appointmentType });
    setCancellationReason("");
    setCancellationComments("");
    setCancellationError("");
    setSavingCancellation(false);
  };

  const closeCancellationModal = () => {
    if (savingCancellation) return;
    setCancellationTarget(null);
    setCancellationReason("");
    setCancellationComments("");
    setCancellationError("");
  };

  const handleCancelAppointment = async (event) => {
    event.preventDefault();
    if (!cancellationTarget || !cancellationReason) return;

    const { appointment, appointmentType } = cancellationTarget;
    setSavingCancellation(true);
    setCancellationError("");

    try {
      if (appointmentType === "checkup") {
        await patientsApi.cancelCheckUp(
          appointment.patientId,
          appointment._id,
          cancellationReason,
          cancellationComments
        );
      } else {
        await followCheckUpsApi.cancel(appointment._id, cancellationReason, cancellationComments);
      }

      if (isPatientDetail) {
        await loadPatientDetail();
      } else if (appointmentType === "checkup") {
        await loadAppointments();
      } else {
        await loadFollowAppointments();
      }
      await loadSummary();

      setCancellationTarget(null);
      setCancellationReason("");
      setCancellationComments("");
    } catch (error) {
      setCancellationError(error.response?.data?.message || "No se pudo cancelar la cita.");
    } finally {
      setSavingCancellation(false);
    }
  };

  const handleCreatePatient = async (event) => {
    event.preventDefault();
    if (patientEmailIsPublic) {
      setPatientsError("El correo debe ser institucional; no se permiten dominios públicos.");
      return;
    }
    setSavingPatient(true);
    setPatientsError("");
    try {
      const payload = {
        branchId: patientForm.branchId,
        name: patientForm.name.trim(),
        gender: patientForm.gender,
        email: patientForm.email.trim(),
        externalId: patientForm.externalId.trim(),
        birthDate: patientForm.birthDate,
        jobArea: patientForm.jobArea.trim(),
        comments: patientForm.comments.trim(),
      };

      await patientsApi.create(payload);
      closePatientModal();
      if (!selectedBranchId && patientForm.branchId) {
        setSelectedBranchId(patientForm.branchId);
      } else {
        await loadPatients();
        await loadSummary();
        await Promise.all([
          loadImcDistribution(),
          loadBodyFatDistribution(),
          loadWaistDistribution(),
          loadVisceralFatDistribution(),
        ]);
      }
    } catch (error) {
      setPatientsError(error.response?.data?.message || "No se pudo crear el paciente.");
      setSavingPatient(false);
    }
  };

  const openAppointmentModal = (patientIdOverride = "") => {
    setAppointmentsError("");
    setLockedAppointmentPatientId(patientIdOverride);
    setAppointmentForm({
      ...EMPTY_APPOINTMENT_FORM,
      patientId: patientIdOverride,
      patientMode: patientIdOverride || appointmentPatientOptions.length ? "existing" : "new",
      newPatient: {
        ...EMPTY_PATIENT_FORM,
        branchId: selectedBranchId || "",
      },
    });
    setIsAppointmentModalOpen(true);
  };

  const closeAppointmentModal = () => {
    setIsAppointmentModalOpen(false);
    setAppointmentForm(EMPTY_APPOINTMENT_FORM);
    setLockedAppointmentPatientId("");
    setSavingAppointment(false);
  };

  const handleCreateAppointment = async (event) => {
    event.preventDefault();
    setSavingAppointment(true);
    setAppointmentsError("");

    try {
      let patientIdForCheckUp = appointmentForm.patientId;

      if (appointmentIsNewPatient) {
        if (appointmentNewPatientEmailIsPublic) {
          setAppointmentsError("El correo debe ser institucional; no se permiten dominios públicos.");
          setSavingAppointment(false);
          return;
        }

        const newPatient = await patientsApi.create({
          branchId: appointmentNewPatient.branchId,
          name: appointmentNewPatient.name.trim(),
          gender: appointmentNewPatient.gender,
          email: appointmentNewPatient.email.trim(),
          externalId: appointmentNewPatient.externalId.trim(),
          birthDate: appointmentNewPatient.birthDate,
          jobArea: appointmentNewPatient.jobArea.trim(),
          comments: appointmentNewPatient.comments.trim(),
        });
        patientIdForCheckUp = newPatient._id;
      }

      await patientsApi.createCheckUp(patientIdForCheckUp, {
        date: normalizeDateTimeLocal(appointmentForm.date),
        status: "pending",
        modality: appointmentForm.modality,
        consultationReasons: appointmentForm.consultationReasons,
        patientObjective: appointmentForm.patientObjective,
        height: appointmentForm.height,
        weight: appointmentForm.weight,
        targetWeight: appointmentForm.targetWeight,
        bodyFatPercentage: appointmentForm.bodyFatPercentage,
        visceralFat: appointmentForm.visceralFat,
        musclePercentage: appointmentForm.musclePercentage,
        waistCm: appointmentForm.waistCm,
        notes: appointmentForm.notes,
        medicalDiagnoses: appointmentForm.medicalDiagnoses,
        planSent: appointmentForm.planSent,
      });
      closeAppointmentModal();
      if (isPatientDetail) {
        await loadPatientDetail();
      } else {
        await loadAppointments();
      }
      await loadSummary();
      await Promise.all([
        loadImcDistribution(),
        loadBodyFatDistribution(),
        loadWaistDistribution(),
        loadVisceralFatDistribution(),
      ]);
    } catch (error) {
      setAppointmentsError(error.response?.data?.message || "No se pudo crear la cita.");
      setSavingAppointment(false);
    }
  };

  const renderBranchSelector = () => (
    <label className={styles.branchSelector}>
      <span>Sucursal</span>
      <select
        className={styles.select}
        value={selectedBranchId}
        onChange={(event) => setSelectedBranchId(event.target.value)}
        disabled={branches.length <= 1}
      >
        {branches.length === 0 && <option value="">Sin sucursales asignadas</option>}
        {branches.length > 1 && <option value="">Todas las sucursales</option>}
        {branches.map((branch) => (
          <option key={branch._id} value={branch._id}>{branch.name || branch._id}</option>
        ))}
      </select>
    </label>
  );

  const renderVerticalTick = ({ x, y, payload }) => {
    const [label, month] = String(payload.value || "").split("|");

    return (
      <g transform={`translate(${x},${y + 8})`}>
        <text
          transform="rotate(-90)"
          textAnchor="end"
          fill="#76797c"
          fontSize="11"
          fontWeight="700"
        >
          <tspan x="0" dy="0">{label}</tspan>
          {month && <tspan x="0" dy="13">{month}</tspan>}
        </text>
      </g>
    );
  };

  const renderJobAreaFilter = (jobArea, onJobAreaChange) => (
    <label className={styles.chartScopeField}>
      <span>Área</span>
      <select value={jobArea} onChange={(event) => onJobAreaChange(event.target.value)}>
        <option value="">Todas las áreas</option>
        {analyticsJobAreas.map((area) => (
          <option key={area} value={area}>{area}</option>
        ))}
      </select>
    </label>
  );

  const renderStackedChartCard = ({
    title,
    data,
    loading,
    stackOrder,
    stackColors,
    stackId,
    scope,
    onScopeChange,
    jobArea,
    onJobAreaChange,
  }) => (
    <article className={styles.chartCard}>
      <div className={styles.chartHeader}>
        <h2 className={styles.sectionTitle}>{title}</h2>
        <div className={styles.chartFilters}>
          <label className={styles.chartScopeField}>
            <span>Género</span>
            <select value={scope} onChange={(event) => onScopeChange(event.target.value)}>
              {DIAGNOSIS_SCOPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          {renderJobAreaFilter(jobArea, onJobAreaChange)}
        </div>
      </div>
      <div className={styles.chartBody} style={{ height: 460 }}>
        {loading ? (
          <div className={styles.chartState}>Cargando datos...</div>
        ) : (
          <div className={styles.chartScroll}>
            <div className={styles.chartInner} style={{ width: Math.max(780, data.length * 140), height: 460 }}>
              <ResponsiveContainer width="100%" height={460}>
                <BarChart data={data} margin={{ top: 82, right: 18, bottom: 12, left: -18 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5edf2" />
                  <XAxis
                    dataKey="name"
                    tick={renderVerticalTick}
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                    height={78}
                  />
                  <YAxis
                    allowDecimals={false}
                    domain={[0, (dataMax) => dataMax + Math.max(2, Math.ceil(dataMax * 0.12))]}
                    tick={{ fill: "#76797c", fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    cursor={{ fill: "rgba(0, 190, 156, 0.08)" }}
                    formatter={(value, name) => [value, name]}
                  />
                  <Legend verticalAlign="top" height={42} />
                  {stackOrder.map((category, index) => (
                    <Bar
                      key={category}
                      dataKey={category}
                      stackId={stackId}
                      fill={stackColors[category]}
                      radius={index === stackOrder.length - 1 ? [6, 6, 0, 0] : [0, 0, 0, 0]}
                      maxBarSize={82}
                    >
                      <LabelList
                        dataKey={category}
                        position="center"
                        fill="#000000"
                        fontSize={12}
                        fontWeight={700}
                        formatter={(value) => (value ? value : "")}
                      />
                      {index === stackOrder.length - 1 && (
                        <LabelList
                          dataKey="total"
                          position="top"
                          offset={12}
                          fill="#042330"
                          fontSize={12}
                          fontWeight={800}
                          formatter={(value) => `Total: ${value}`}
                        />
                      )}
                    </Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </article>
  );

  const renderBarChartCard = ({
    title,
    data,
    loading,
    series,
    yAxisProps = {},
    labelFormatter,
    chartHeight = 380,
    xAxisHeight = 78,
    chartWidthPerItem = 150,
    margin = { top: 58, right: 18, bottom: 12, left: -18 },
    jobArea,
    onJobAreaChange,
  }) => (
    <article className={styles.chartCard}>
      <div className={styles.chartHeader}>
        <h2 className={styles.sectionTitle}>{title}</h2>
        {onJobAreaChange && renderJobAreaFilter(jobArea, onJobAreaChange)}
      </div>
      <div className={styles.chartBody} style={{ height: chartHeight }}>
        {loading ? (
          <div className={styles.chartState}>Cargando datos...</div>
        ) : (
          <div className={styles.chartScroll}>
            <div className={styles.chartInner} style={{ width: Math.max(780, data.length * chartWidthPerItem), height: chartHeight }}>
              <ResponsiveContainer width="100%" height={chartHeight}>
                <BarChart
                  data={data}
                  margin={margin}
                  barGap={0}
                  barCategoryGap="40%"
                  barSize={34}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5edf2" />
                  <XAxis
                    dataKey="name"
                    tick={renderVerticalTick}
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                    height={xAxisHeight}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fill: "#76797c", fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    {...yAxisProps}
                  />
                  <Tooltip
                    cursor={{ fill: "rgba(0, 190, 156, 0.08)" }}
                    formatter={(value, name) => [value, name]}
                  />
                  <Legend verticalAlign="top" height={42} />
                  {series.map((item) => (
                    <Bar
                      key={item.key}
                      dataKey={item.key}
                      name={item.label}
                      fill={item.color}
                      radius={[6, 6, 0, 0]}
                    >
                      <LabelList
                        dataKey={item.key}
                        position="top"
                        fill="#000000"
                        fontSize={12}
                        fontWeight={700}
                        formatter={labelFormatter || ((value) => (value ? value : ""))}
                      />
                    </Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </article>
  );

  const renderAnalyticsFilters = () => (
    <section className={styles.filtersPanel}>
      <label className={styles.filterField}>
        <span>Año</span>
        <select value={analyticsYear} onChange={(event) => handleAnalyticsYearChange(event.target.value)}>
          {availableYears.map((year) => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
      </label>

      <div className={styles.filterGroup}>
        <span>Trimestres</span>
        <div className={styles.filterOptions}>
          {availableQuarters.map((quarter) => (
            <label className={styles.filterCheck} key={quarter.value}>
              <input
                type="checkbox"
                checked={selectedQuarters.includes(quarter.value)}
                onChange={() => handleQuarterToggle(quarter.value)}
              />
              <span>{quarter.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className={styles.filterGroup}>
        <span>Meses visibles</span>
        <div className={styles.filterOptions}>
          {availableMonths.map((month) => (
            <label className={styles.filterCheck} key={month.value}>
              <input
                type="checkbox"
                checked={selectedMonths.includes(month.value)}
                onChange={() => handleMonthToggle(month.value)}
              />
              <span>{month.short}</span>
            </label>
          ))}
        </div>
      </div>
    </section>
  );

  const renderHistoricalPreviewTable = () => {
    const rows = historicalPreview?.preview?.[historicalPreviewTab] || [];
    const columnMap = {
      patients: [
        ["action", "Accion"],
        ["workbookPatientId", "ID XLSX"],
        ["externalId", "ID interno"],
        ["name", "Paciente"],
        ["email", "Correo"],
        ["gender", "Genero"],
        ["age", "Edad"],
        ["jobArea", "Area"],
      ],
      firstCheckUps: [
        ["action", "Accion"],
        ["workbookPatientId", "ID XLSX"],
        ["patientName", "Paciente"],
        ["date", "Fecha"],
        ["modality", "Modalidad"],
        ["weight", "Peso"],
        ["height", "Estatura"],
        ["imc", "IMC"],
        ["imcDiagnosis", "Dx IMC"],
      ],
      followCheckUps: [
        ["action", "Accion"],
        ["workbookPatientId", "ID XLSX"],
        ["patientName", "Paciente"],
        ["date", "Fecha"],
        ["consultationNumber", "Consulta"],
        ["modality", "Modalidad"],
        ["weight", "Peso"],
        ["imc", "IMC"],
        ["bodyFatPercentage", "% grasa"],
      ],
    };
    const columns = columnMap[historicalPreviewTab] || columnMap.patients;

    if (!rows.length) {
      return <div className={styles.tableState}>No hay registros para esta pestaña.</div>;
    }

    return (
      <div className={styles.tableScroll}>
        <table className={styles.patientTable}>
          <thead>
            <tr>
              <th>Fila</th>
              {columns.map(([, label]) => (
                <th key={label}>{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${historicalPreviewTab}-${row.row}-${row.patientName || row.name}`}>
                <td>{row.row}</td>
                {columns.map(([key]) => (
                  <td key={key}>
                    {key === "action"
                      ? row[key] === "create" ? "Crear" : "Actualizar"
                      : key === "date" && row[key]
                        ? formatDateTime(row[key])
                        : row[key] ?? "Sin registro"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderHistoricalErrorDetails = () => {
    if (!historicalErrorDetails.length) return null;

    return (
      <div className={styles.errorDetails}>
        <h3>Datos a corregir</h3>
        <div className={styles.tableScroll}>
          <table className={styles.patientTable}>
            <thead>
              <tr>
                <th>Hoja</th>
                <th>Fila</th>
                <th>Error</th>
                <th>Dato</th>
              </tr>
            </thead>
            <tbody>
              {historicalErrorDetails.map((item, index) => (
                <tr key={`${item.sheet || "sheet"}-${item.row || "row"}-${index}`}>
                  <td>{item.sheet || "Sin hoja"}</td>
                  <td>{item.row || "General"}</td>
                  <td>{(item.errors || []).join("; ")}</td>
                  <td>
                    {item.data
                      ? Object.entries(item.data)
                        .filter(([, value]) => value !== undefined && value !== null && value !== "")
                        .map(([key, value]) => `${key}: ${value}`)
                        .join(" | ")
                      : "Sin dato"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderHistoricalUploadSection = () => {
    const summary = historicalPreview?.summary || {};

    return (
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Carga de historico XLSX</h2>
        </div>

        {historicalError && <div className={styles.alert}>{historicalError}</div>}
        {renderHistoricalErrorDetails()}

        <div
          className={`${styles.uploadZone} ${historicalDragActive ? styles.uploadZoneActive : ""}`}
          onDragOver={(event) => {
            event.preventDefault();
            setHistoricalDragActive(true);
          }}
          onDragLeave={() => setHistoricalDragActive(false)}
          onDrop={handleHistoricalDrop}
        >
          <input
            key={historicalInputKey}
            id="historical-file"
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={handleHistoricalFileInput}
          />
          <label htmlFor="historical-file">
            <strong>{historicalFile ? historicalFile.name : "Seleccionar archivo XLSX"}</strong>
            <span>Arrastra el archivo aqui o seleccionalo desde tu equipo.</span>
          </label>
        </div>

        <div className={styles.modalActions}>
          <button className={styles.secondaryButton} type="button" onClick={resetHistoricalUpload}>
            Cancelar
          </button>
          <button
            className={styles.primaryButton}
            type="button"
            onClick={handleHistoricalPreview}
            disabled={historicalLoading || !historicalFile || !selectedBranchId}
            title={!selectedBranchId ? "Selecciona una sucursal" : undefined}
          >
            {historicalLoading ? "Leyendo..." : "Aceptar"}
          </button>
        </div>

        {historicalPreview && (
          <>
            <div className={styles.historicalSummaryGrid}>
              <article className={styles.card}>
                <span className={styles.cardValue}>{summary.createdPatients || 0}</span>
                <span className={styles.cardLabel}>Pacientes nuevos</span>
              </article>
              <article className={styles.card}>
                <span className={styles.cardValue}>{summary.createdFirstCheckUps || 0}</span>
                <span className={styles.cardLabel}>Primeras nuevas</span>
              </article>
              <article className={styles.card}>
                <span className={styles.cardValue}>{summary.createdFollowCheckUps || 0}</span>
                <span className={styles.cardLabel}>Seguimientos nuevos</span>
              </article>
            </div>

            <div className={styles.previewTabs} role="tablist" aria-label="Previsualizacion historico">
              {HISTORICAL_TABS.map((tab) => (
                <button
                  key={tab.key}
                  className={`${styles.previewTab} ${historicalPreviewTab === tab.key ? styles.previewTabActive : ""}`}
                  type="button"
                  onClick={() => setHistoricalPreviewTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className={styles.tableCard}>
              {renderHistoricalPreviewTable()}
            </div>

            <div className={styles.modalActions}>
              <button className={styles.secondaryButton} type="button" onClick={resetHistoricalUpload}>
                Cancelar
              </button>
              <button
                className={styles.primaryButton}
                type="button"
                onClick={handleHistoricalCommit}
                disabled={historicalSaving}
              >
                {historicalSaving ? "Guardando..." : "Aceptar carga"}
              </button>
            </div>
          </>
        )}
      </section>
    );
  };

  const renderGeneralSection = () => (
    <section className={styles.section}>
      {renderAnalyticsFilters()}
      <div className={styles.chartsGrid}>
        {renderBarChartCard({
          title: "Consultas Totales",
          data: totalsChartData,
          loading: summaryLoading,
          series: TOTALS_BAR_SERIES,
          jobArea: chartJobAreaFilters.totals,
          onJobAreaChange: (jobArea) => handleChartJobAreaChange("totals", jobArea),
        })}
        {renderBarChartCard({
          title: "Distribución de género de pacientes atendidos",
          data: genderChartData,
          loading: summaryLoading,
          series: GENDER_BAR_SERIES,
          jobArea: chartJobAreaFilters.gender,
          onJobAreaChange: (jobArea) => handleChartJobAreaChange("gender", jobArea),
        })}
        {renderStackedChartCard({
          title: "Evolucion del IMC (v2)",
          data: imcEvolutionStackedChartData,
          loading: imcLoading,
          stackOrder: IMC_EVOLUTION_LINE_ORDER,
          stackColors: IMC_STACK_COLORS,
          stackId: "imcEvolution",
          scope: diagnosisScopes.imc,
          onScopeChange: (scope) => handleDiagnosisScopeChange("imc", scope),
          jobArea: chartJobAreaFilters.imc,
          onJobAreaChange: (jobArea) => handleChartJobAreaChange("imc", jobArea),
        })}
        {renderStackedChartCard({
          title: "Porcentaje de Grasa",
          data: bodyFatChartData,
          loading: bodyFatLoading,
          stackOrder: BODY_FAT_STACK_ORDER,
          stackColors: BODY_FAT_STACK_COLORS,
          stackId: "bodyFat",
          scope: diagnosisScopes.bodyFat,
          onScopeChange: (scope) => handleDiagnosisScopeChange("bodyFat", scope),
          jobArea: chartJobAreaFilters.bodyFat,
          onJobAreaChange: (jobArea) => handleChartJobAreaChange("bodyFat", jobArea),
        })}
        {renderStackedChartCard({
          title: "Circunferencia de la Cintura",
          data: waistChartData,
          loading: waistLoading,
          stackOrder: WAIST_STACK_ORDER,
          stackColors: WAIST_STACK_COLORS,
          stackId: "waist",
          scope: diagnosisScopes.waist,
          onScopeChange: (scope) => handleDiagnosisScopeChange("waist", scope),
          jobArea: chartJobAreaFilters.waist,
          onJobAreaChange: (jobArea) => handleChartJobAreaChange("waist", jobArea),
        })}
        {renderStackedChartCard({
          title: "Grasa Visceral",
          data: visceralFatChartData,
          loading: visceralFatLoading,
          stackOrder: VISCERAL_FAT_STACK_ORDER,
          stackColors: VISCERAL_FAT_STACK_COLORS,
          stackId: "visceralFat",
          scope: diagnosisScopes.visceralFat,
          onScopeChange: (scope) => handleDiagnosisScopeChange("visceralFat", scope),
          jobArea: chartJobAreaFilters.visceralFat,
          onJobAreaChange: (jobArea) => handleChartJobAreaChange("visceralFat", jobArea),
        })}
      </div>
    </section>
  );

  const renderGeneralEvaluationSection = () => (
    <section className={styles.section}>
      {renderAnalyticsFilters()}
      {reviewAnalyticsError && <div className={styles.alert}>{reviewAnalyticsError}</div>}

      <div className={`${styles.chartsGrid} ${styles.evaluationChartsGrid}`}>
        {renderBarChartCard({
          title: "Promedio general por mes",
          data: reviewAverageMonthlyChartData,
          loading: reviewAnalyticsLoading,
          series: REVIEW_AVERAGE_SERIES,
          yAxisProps: { allowDecimals: true, domain: [0, 5], ticks: [0, 1, 2, 3, 4, 5] },
          labelFormatter: (value) => (value ? Number(value).toFixed(1).replace(".0", "") : ""),
          chartHeight: 380,
          xAxisHeight: 78,
          chartWidthPerItem: 150,
          margin: { top: 64, right: 24, bottom: 12, left: -18 },
          jobArea: chartJobAreaFilters.reviewAverage,
          onJobAreaChange: (jobArea) => handleChartJobAreaChange("reviewAverage", jobArea),
        })}
        {renderBarChartCard({
          title: "Calificacion general",
          data: reviewQuestionMonthlyChartData,
          loading: reviewAnalyticsLoading,
          series: REVIEW_QUESTION_SERIES,
          yAxisProps: { allowDecimals: true, domain: [0, 5], ticks: [0, 1, 2, 3, 4, 5] },
          labelFormatter: (value) => (value ? Number(value).toFixed(1).replace(".0", "") : ""),
          chartHeight: 380,
          xAxisHeight: 78,
          chartWidthPerItem: 170,
          margin: { top: 64, right: 24, bottom: 12, left: -18 },
          jobArea: chartJobAreaFilters.reviewQuestions,
          onJobAreaChange: (jobArea) => handleChartJobAreaChange("reviewQuestions", jobArea),
        })}
      </div>

      <div className={styles.reviewCommentsCard}>
        <div className={styles.sectionHeader}>
          <div>
            <h2 className={styles.sectionTitle}>Comentarios</h2>
            <p className={styles.sectionSubtitle}>{reviewAnalytics.count || 0} evaluaciones en el periodo.</p>
          </div>
        </div>

        {reviewAnalyticsLoading ? (
          <div className={styles.chartState}>Cargando comentarios...</div>
        ) : reviewAnalytics.comments.length ? (
          <div className={styles.tableScroll}>
            <table className={styles.patientTable}>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Comentario</th>
                </tr>
              </thead>
              <tbody>
                {reviewAnalytics.comments.map((review, index) => (
                  <tr key={`${review.createdAt || "review"}-${index}`}>
                    <td>{formatDateTime(review.createdAt)}</td>
                    <td>{review.comments}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={styles.emptyState}>Sin comentarios para el periodo seleccionado.</div>
        )}
      </div>
    </section>
  );

  const renderPatientsSection = () => (
    <>
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Pacientes registrados</h2>
          <button
            className={styles.primaryButton}
            type="button"
            onClick={openPatientModal}
            disabled={!selectedBranchId}
            title={!selectedBranchId ? "Selecciona una sucursal" : undefined}
          >
            Agregar paciente
          </button>
        </div>

        {patientsError && <div className={styles.alert}>{patientsError}</div>}

        <div className={styles.tableFilterBar} aria-label="Filtros de pacientes">
          <label className={styles.tableFilterField}>
            <span>Nombre</span>
            <input
              type="search"
              value={patientTableFilters.name}
              onChange={(event) => handlePatientTableFilterChange("name", event.target.value)}
              placeholder="Buscar mientras escribes"
            />
          </label>
          <label className={styles.tableFilterField}>
            <span>Género</span>
            <select
              value={patientTableFilters.gender}
              onChange={(event) => handlePatientTableFilterChange("gender", event.target.value)}
            >
              <option value="">Todos</option>
              <option value="femenino">Femenino</option>
              <option value="masculino">Masculino</option>
            </select>
          </label>
          <label className={styles.tableFilterField}>
            <span>Correo</span>
            <input
              type="search"
              value={patientTableFilters.email}
              onChange={(event) => handlePatientTableFilterChange("email", event.target.value)}
              placeholder="Buscar correo"
            />
          </label>
          <label className={styles.tableFilterField}>
            <span>Área</span>
            <select
              value={patientTableFilters.jobArea}
              onChange={(event) => handlePatientTableFilterChange("jobArea", event.target.value)}
            >
              <option value="">Todas</option>
              {patientJobAreaOptions.map((jobArea) => (
                <option key={jobArea} value={jobArea}>{jobArea}</option>
              ))}
            </select>
          </label>
          <button
            className={styles.clearFiltersButton}
            type="button"
            onClick={() => {
              setPatientTableFilters(EMPTY_PATIENT_TABLE_FILTERS);
              setPatientTablePage(1);
            }}
          >
            Limpiar filtros
          </button>
        </div>

        <div className={styles.tableCard}>
          {patientsLoading ? (
            <div className={styles.tableState}>Cargando pacientes...</div>
          ) : patients.length === 0 ? (
            <div className={styles.tableState}>No hay pacientes registrados.</div>
          ) : filteredPatients.length === 0 ? (
            <div className={styles.tableState}>No hay pacientes que coincidan con los filtros.</div>
          ) : (
            <>
              <div className={styles.tableScroll}>
                <table className={styles.patientTable}>
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Genero</th>
                    <th>Correo</th>
                    <th>Area</th>
                    <th>Edad</th>
                    <th>Sucursal</th>
                    <th>Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedPatients.map((patient) => (
                    <tr key={patient._id}>
                      <td>
                        <strong>{patient.name}</strong>
                        {patient.externalId && <span>{patient.externalId}</span>}
                      </td>
                      <td>{formatGender(patient.gender)}</td>
                      <td>{patient.email || "Sin registro"}</td>
                      <td>{patient.jobArea || "Sin registro"}</td>
                      <td>{patient.age ?? "Sin registro"}</td>
                      <td>{branchNameById[String(patient.branchId)] || "Sucursal"}</td>
                      <td>
                        <button
                          className={styles.tableActionButton}
                          type="button"
                          onClick={() => navigate(`/nutrilogyPanel/patients/${patient._id}`)}
                        >
                          Ver detalle
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                </table>
              </div>
              {renderTablePagination({
                currentPage: safePatientPage,
                pageCount: patientPageCount,
                totalItems: filteredPatients.length,
                onPageChange: setPatientTablePage,
              })}
            </>
          )}
        </div>
      </section>

      {isPatientModalOpen && (
        <div className={styles.modalBackdrop} role="presentation">
          <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="patient-modal-title">
            <div className={styles.modalHeader}>
              <div>
                <h2 id="patient-modal-title" className={styles.modalTitle}>Agregar paciente</h2>
                <p className={styles.modalSubtitle}>Registra los datos base del directorio.</p>
              </div>
              <button className={styles.iconButton} type="button" onClick={closePatientModal} aria-label="Cerrar">
                x
              </button>
            </div>

            <form className={styles.form} onSubmit={handleCreatePatient}>
              <label className={styles.field}>
                <span>Sucursal *</span>
                <select
                  value={patientForm.branchId}
                  disabled
                  required
                >
                  <option value={selectedBranchId}>{selectedBranch?.name || selectedBranchId}</option>
                </select>
              </label>

              <label className={styles.field}>
                <span>Nombre completo *</span>
                <input
                  type="text"
                  value={patientForm.name}
                  onChange={(event) => handlePatientFormChange("name", event.target.value)}
                  required
                />
              </label>

              <label className={styles.field}>
                <span>Genero *</span>
                <select
                  value={patientForm.gender}
                  onChange={(event) => handlePatientFormChange("gender", event.target.value)}
                  required
                >
                  <option value="">Selecciona genero</option>
                  <option value="femenino">Femenino</option>
                  <option value="masculino">Masculino</option>
                </select>
              </label>

              <label className={styles.field}>
                <span>Correo institucional</span>
                <input
                  type="email"
                  value={patientForm.email}
                  onChange={(event) => handlePatientFormChange("email", event.target.value)}
                  aria-invalid={patientEmailIsPublic}
                />
                {patientEmailIsPublic && (
                  <small className={styles.fieldError}>No se permiten correos de dominios públicos.</small>
                )}
              </label>

              <label className={styles.field}>
                <span>ID colaborador</span>
                <input
                  type="text"
                  value={patientForm.externalId}
                  onChange={(event) => handlePatientFormChange("externalId", event.target.value)}
                />
              </label>

              <label className={styles.field}>
                <span>Fecha de nacimiento</span>
                <input
                  type="date"
                  value={patientForm.birthDate}
                  onChange={(event) => handlePatientFormChange("birthDate", event.target.value)}
                />
              </label>

              <label className={styles.field}>
                <span>Area de trabajo</span>
                <select
                  value={patientForm.jobArea}
                  onChange={(event) => handlePatientFormChange("jobArea", event.target.value)}
                >
                  <option value="">Selecciona area</option>
                  {selectedBranchJobAreas.map((jobArea) => (
                    <option key={jobArea} value={jobArea}>{jobArea}</option>
                  ))}
                </select>
              </label>

              <label className={`${styles.field} ${styles.fieldFull}`}>
                <span>Comentarios</span>
                <textarea
                  value={patientForm.comments}
                  onChange={(event) => handlePatientFormChange("comments", event.target.value)}
                  rows="3"
                />
              </label>

              <div className={styles.modalActions}>
                <button className={styles.secondaryButton} type="button" onClick={closePatientModal}>
                  Cancelar
                </button>
                <button className={styles.primaryButton} type="submit" disabled={savingPatient || patientEmailIsPublic}>
                  {savingPatient ? "Guardando..." : "Guardar paciente"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );

  const renderMultiOptionGroup = ({ label, field, options, value, onToggle }) => {
    const selectedOptions = splitMultiValue(value);

    return (
      <fieldset className={`${styles.optionGroup} ${styles.fieldFull}`}>
        <span>{label}</span>
        <div className={styles.optionGrid}>
          {options.map((option) => (
            <label className={styles.optionCheck} key={option}>
              <input
                type="checkbox"
                checked={selectedOptions.includes(option)}
                onChange={() => onToggle(field, option)}
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      </fieldset>
    );
  };

  const renderAppointmentDetail = (appointment) => {
    const draft = appointmentDrafts[appointment._id] || getCheckUpDraft(appointment);
    const isSaving = savingCheckUpId === appointment._id;
    const isCancelled = isAppointmentCancelled(appointment);
    const calculatorInput = {
      gender: appointment.patientGender,
      weight: draft.weight,
      height: draft.height,
      age: draft.age,
      activityFactor: draft.activityFactor,
    };
    const harrisBenedictResult = calculateCaloricExpenditure({
      ...calculatorInput,
      formula: "harris-benedict",
    });
    const mifflinResult = calculateCaloricExpenditure({
      ...calculatorInput,
      formula: "mifflin-st-jeor",
    });

    const selectCaloricResult = (formula, result) => {
      if (!result) return;
      handleAppointmentDraftChange(appointment._id, "calConsumption", result.totalExpenditure);
      handleAppointmentDraftChange(appointment._id, "calorieFormula", formula);
    };

    return (
      <div className={styles.appointmentDetail}>
        <div className={styles.detailHeader}>
          <div>
            <h3>Datos de primera consulta</h3>
            <p>{appointment.patientName}</p>
          </div>
          <button
            className={styles.primaryButton}
            type="button"
            onClick={() => handleUpdateAppointmentCheckUp(appointment)}
            disabled={isSaving || isCancelled}
          >
            {isCancelled ? "Cita cancelada" : isSaving ? "Actualizando..." : "Actualizar checkup"}
          </button>
        </div>

        <div className={styles.detailGrid}>
          <label className={styles.field}>
            <span>Fecha y hora *</span>
            <input
              type="datetime-local"
              value={draft.date}
              onChange={(event) => handleAppointmentDraftChange(appointment._id, "date", event.target.value)}
              required
            />
          </label>

          <label className={styles.field}>
            <span>Modalidad</span>
            <select
              value={draft.modality}
              onChange={(event) => handleAppointmentDraftChange(appointment._id, "modality", event.target.value)}
            >
              <option value="">Selecciona modalidad</option>
              {MODALITY_OPTIONS.map((modality) => (
                <option key={modality} value={modality}>{modality}</option>
              ))}
            </select>
          </label>

          <label className={styles.field}>
            <span>Peso inicial (kg)</span>
            <input
              type="number"
              min="0"
              step="0.1"
              value={draft.weight}
              onChange={(event) => handleAppointmentDraftChange(appointment._id, "weight", event.target.value)}
            />
          </label>

          <label className={styles.field}>
            <span>Estatura (m)</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={draft.height}
              onChange={(event) => handleAppointmentDraftChange(appointment._id, "height", event.target.value)}
            />
          </label>

          <label className={styles.field}>
            <span>Edad (años)</span>
            <input
              type="number"
              min="0"
              step="1"
              value={draft.age}
              onChange={(event) => handleAppointmentDraftChange(appointment._id, "age", event.target.value)}
            />
          </label>

          <label className={styles.field}>
            <span>Peso meta (kg)</span>
            <input
              type="number"
              min="0"
              step="0.1"
              value={draft.targetWeight}
              onChange={(event) => handleAppointmentDraftChange(appointment._id, "targetWeight", event.target.value)}
            />
          </label>

          <label className={styles.field}>
            <span>% grasa inicial</span>
            <input
              type="number"
              min="0"
              step="0.1"
              value={draft.bodyFatPercentage}
              onChange={(event) => handleAppointmentDraftChange(appointment._id, "bodyFatPercentage", event.target.value)}
            />
          </label>

          <label className={styles.field}>
            <span>Grasa visceral</span>
            <input
              type="number"
              min="0"
              step="0.1"
              value={draft.visceralFat}
              onChange={(event) => handleAppointmentDraftChange(appointment._id, "visceralFat", event.target.value)}
            />
          </label>

          <label className={styles.field}>
            <span>% musculo inicial</span>
            <input
              type="number"
              min="0"
              step="0.1"
              value={draft.musclePercentage}
              onChange={(event) => handleAppointmentDraftChange(appointment._id, "musclePercentage", event.target.value)}
            />
          </label>

          <label className={styles.field}>
            <span>Cintura inicial (cm)</span>
            <input
              type="number"
              min="0"
              step="0.1"
              value={draft.waistCm}
              onChange={(event) => handleAppointmentDraftChange(appointment._id, "waistCm", event.target.value)}
            />
          </label>

          {renderMultiOptionGroup({
            label: "Motivo de consulta",
            field: "consultationReasons",
            options: CONSULTATION_REASON_OPTIONS,
            value: draft.consultationReasons,
            onToggle: (field, option) => handleAppointmentDraftOptionToggle(appointment._id, field, option),
          })}

          {renderMultiOptionGroup({
            label: "Objetivo del paciente",
            field: "patientObjective",
            options: PATIENT_OBJECTIVE_OPTIONS,
            value: draft.patientObjective,
            onToggle: (field, option) => handleAppointmentDraftOptionToggle(appointment._id, field, option),
          })}

          <label className={`${styles.field} ${styles.fieldFull}`}>
            <span>Notas</span>
            <textarea
              rows="2"
              value={draft.notes}
              onChange={(event) => handleAppointmentDraftChange(appointment._id, "notes", event.target.value)}
            />
          </label>

          <label className={`${styles.field} ${styles.fieldFull}`}>
            <span>Diagnosticos medicos</span>
            <textarea
              rows="2"
              value={draft.medicalDiagnoses}
              onChange={(event) => handleAppointmentDraftChange(appointment._id, "medicalDiagnoses", event.target.value)}
            />
          </label>

          <label className={`${styles.checkboxField} ${styles.fieldFull}`}>
            <input
              type="checkbox"
              checked={draft.planSent}
              onChange={(event) => handleAppointmentDraftChange(appointment._id, "planSent", event.target.checked)}
            />
            <span>Plan enviado</span>
          </label>
        </div>

        <section className={styles.calorieCalculatorPanel}>
          <div className={styles.calorieCalculatorHeader}>
            <div>
              <h4>Calculadoras de gasto calórico</h4>
              <p>
                Se usa el sexo {formatGender(appointment.patientGender).toLowerCase()}, junto con peso,
                estatura y edad. Selecciona un factor de actividad para comparar ambas fórmulas.
              </p>
            </div>
            <label className={styles.calorieActivityField}>
              <span>Factor de actividad</span>
              <select
                value={draft.activityFactor}
                onChange={(event) => handleAppointmentDraftChange(appointment._id, "activityFactor", event.target.value)}
                disabled={isCancelled}
              >
                <option value="">Selecciona un nivel</option>
                {ACTIVITY_FACTOR_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label} ({option.value}) — {option.detail}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {!harrisBenedictResult && (
            <p className={styles.calorieCalculatorHint}>
              Completa peso, estatura y edad; verifica que el paciente tenga sexo registrado y selecciona
              un factor de actividad.
            </p>
          )}

          <div className={styles.calorieCalculatorGrid}>
            {[
              { key: "harris-benedict", name: "Harris-Benedict", result: harrisBenedictResult },
              { key: "mifflin-st-jeor", name: "Mifflin-St Jeor", result: mifflinResult },
            ].map(({ key, name, result }) => (
              <article className={styles.calorieCalculatorCard} key={key}>
                <h5>{name}</h5>
                <div className={styles.calorieCalculatorValues}>
                  <span>
                    <strong>TMB</strong>
                    {result ? `${result.basalRate.toLocaleString("es-MX")} kcal/día` : "Pendiente"}
                  </span>
                  <span>
                    <strong>GET</strong>
                    {result ? `${result.totalExpenditure.toLocaleString("es-MX")} kcal/día` : "Pendiente"}
                  </span>
                </div>
                <button
                  className={styles.calorieSelectButton}
                  type="button"
                  onClick={() => selectCaloricResult(key, result)}
                  disabled={!result || isCancelled}
                >
                  {draft.calorieFormula === key ? "Resultado seleccionado" : "Usar este resultado"}
                </button>
              </article>
            ))}
          </div>

          <div className={styles.selectedCalorieResult}>
            <span>
              <strong>Consumo calórico seleccionado</strong>
              {draft.calConsumption !== "" && draft.calConsumption !== null
                ? `${Number(draft.calConsumption).toLocaleString("es-MX")} kcal/día`
                : "Sin seleccionar (opcional)"}
            </span>
            {draft.calConsumption !== "" && draft.calConsumption !== null && (
              <button
                type="button"
                onClick={() => {
                  handleAppointmentDraftChange(appointment._id, "calConsumption", "");
                  handleAppointmentDraftChange(appointment._id, "calorieFormula", "");
                }}
                disabled={isCancelled}
              >
                Limpiar
              </button>
            )}
          </div>
        </section>

        <div className={styles.calculatedPanel}>
          <h4>Calculados</h4>
          <div className={styles.calculatedGrid}>
            <span><strong>Mes</strong>{formatCheckUpValue(appointment.month)}</span>
            <span><strong>IMC</strong>{formatCheckUpValue(appointment.imc)}</span>
            <span><strong>Dx IMC</strong>{formatCheckUpValue(appointment.imcDiagnosis)}</span>
            <span><strong>Rango peso saludable</strong>{formatCheckUpValue(appointment.healthyWeightRange)}</span>
            <span><strong>Kg grasa</strong>{formatCheckUpValue(appointment.bodyFatKg)}</span>
            <span><strong>Dx % grasa</strong>{formatCheckUpValue(appointment.bodyFatDiagnosis)}</span>
            <span><strong>Dx visceral</strong>{formatCheckUpValue(appointment.visceralFatDiagnosis)}</span>
            <span><strong>Kg musculo</strong>{formatCheckUpValue(appointment.muscleKg)}</span>
            <span><strong>Dx cintura</strong>{formatCheckUpValue(appointment.waistDiagnosis)}</span>
            <span><strong>Consumo calórico</strong>{formatCheckUpValue(appointment.calConsumption, " kcal/día")}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderFollowPlainFields = ({ draft, onChange, includePatient = false }) => (
    <>
      {includePatient && (
        <label className={styles.field}>
          <span>Paciente *</span>
          <select
            value={draft.patientId}
            onChange={(event) => onChange("patientId", event.target.value)}
            required
            disabled={Boolean(lockedFollowPatientId)}
          >
            <option value="">Selecciona paciente</option>
            {followPatientOptions.map((patient) => (
              <option key={patient._id} value={patient._id}>{patient.name}</option>
            ))}
          </select>
        </label>
      )}

      <label className={styles.field}>
        <span>Fecha y hora *</span>
        <input
          type="datetime-local"
          value={draft.date}
          onChange={(event) => onChange("date", event.target.value)}
          required
        />
      </label>

      <label className={styles.field}>
        <span>Modalidad *</span>
        <select
          value={draft.modality}
          onChange={(event) => onChange("modality", event.target.value)}
          required
        >
          <option value="">Selecciona modalidad</option>
          {MODALITY_OPTIONS.map((modality) => (
            <option key={modality} value={modality}>{modality}</option>
          ))}
        </select>
      </label>

      <label className={styles.field}>
        <span>Peso actual *</span>
        <input
          type="number"
          min="0"
          step="0.1"
          value={draft.weight}
          onChange={(event) => onChange("weight", event.target.value)}
          required
        />
      </label>

      <label className={styles.field}>
        <span>% grasa actual *</span>
        <input
          type="number"
          min="0"
          step="0.1"
          value={draft.bodyFatPercentage}
          onChange={(event) => onChange("bodyFatPercentage", event.target.value)}
          required
        />
      </label>

      <label className={styles.field}>
        <span>Grasa visceral actual *</span>
        <input
          type="number"
          min="0"
          step="0.1"
          value={draft.visceralFat}
          onChange={(event) => onChange("visceralFat", event.target.value)}
          required
        />
      </label>

      <label className={styles.field}>
        <span>% musculo actual *</span>
        <input
          type="number"
          min="0"
          step="0.1"
          value={draft.musclePercentage}
          onChange={(event) => onChange("musclePercentage", event.target.value)}
          required
        />
      </label>

      <label className={styles.field}>
        <span>Cintura actual (cm) *</span>
        <input
          type="number"
          min="0"
          step="0.1"
          value={draft.waistCm}
          onChange={(event) => onChange("waistCm", event.target.value)}
          required
        />
      </label>

      <label className={styles.field}>
        <span>Cumplimiento *</span>
        <select
          value={draft.compliance}
          onChange={(event) => onChange("compliance", event.target.value)}
          required
        >
          <option value="">Selecciona cumplimiento</option>
          {COMPLIANCE_OPTIONS.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      </label>

      <label className={styles.field}>
        <span>Motivo falta de apego *</span>
        <select
          value={draft.lackOfAdherenceReason}
          onChange={(event) => onChange("lackOfAdherenceReason", event.target.value)}
          required
        >
          <option value="">Selecciona motivo</option>
          {LACK_OF_ADHERENCE_OPTIONS.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      </label>

      <label className={`${styles.field} ${styles.fieldFull}`}>
        <span>Comentarios</span>
        <textarea
          rows="2"
          value={draft.comments}
          onChange={(event) => onChange("comments", event.target.value)}
        />
      </label>

      <label className={`${styles.checkboxField} ${styles.fieldFull}`}>
        <input
          type="checkbox"
          checked={draft.planSent}
          onChange={(event) => onChange("planSent", event.target.checked)}
        />
        <span>Plan enviado</span>
      </label>
    </>
  );

  const renderFollowDetail = (followAppointment) => {
    const draft = followDrafts[followAppointment._id] || getFollowDraft(followAppointment);
    const isSaving = savingFollowCheckUpId === followAppointment._id;
    const isCancelled = isAppointmentCancelled(followAppointment);

    return (
      <div className={styles.appointmentDetail}>
        <div className={styles.detailHeader}>
          <div>
            <h3>Datos de seguimiento</h3>
            <p>{followAppointment.patientName}</p>
          </div>
          <button
            className={styles.primaryButton}
            type="button"
            onClick={() => handleUpdateFollowAppointment(followAppointment)}
            disabled={isSaving || isCancelled}
          >
            {isCancelled ? "Cita cancelada" : isSaving ? "Actualizando..." : "Actualizar seguimiento"}
          </button>
        </div>

        <div className={styles.detailGrid}>
          {renderFollowPlainFields({
            draft,
            onChange: (field, value) => handleFollowDraftChange(followAppointment._id, field, value),
          })}
        </div>

        <div className={styles.calculatedPanel}>
          <h4>Calculados</h4>
          <div className={styles.calculatedGrid}>
            <span><strong>Mes</strong>{formatCheckUpValue(followAppointment.month)}</span>
            <span><strong>Num consulta</strong>{formatCheckUpValue(followAppointment.consultationNumber)}</span>
            <span><strong>Diferencia peso</strong>{formatCheckUpValue(followAppointment.weightDifference)}</span>
            <span><strong>IMC actual</strong>{formatCheckUpValue(followAppointment.imc)}</span>
            <span><strong>Dx IMC</strong>{formatCheckUpValue(followAppointment.imcDiagnosis)}</span>
            <span><strong>Kg grasa</strong>{formatCheckUpValue(followAppointment.bodyFatKg)}</span>
            <span><strong>Dx % grasa</strong>{formatCheckUpValue(followAppointment.bodyFatDiagnosis)}</span>
            <span><strong>Dif grasa kg</strong>{formatCheckUpValue(followAppointment.bodyFatKgDifference)}</span>
            <span><strong>Dif visceral</strong>{formatCheckUpValue(followAppointment.visceralFatDifference)}</span>
            <span><strong>Kg musculo</strong>{formatCheckUpValue(followAppointment.muscleKg)}</span>
            <span><strong>Dif musculo kg</strong>{formatCheckUpValue(followAppointment.muscleKgDifference)}</span>
            <span><strong>Dx cintura</strong>{formatCheckUpValue(followAppointment.waistDiagnosis)}</span>
            <span><strong>Diferencia cintura</strong>{formatCheckUpValue(followAppointment.waistDifference)}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderCancellationAction = (appointment, appointmentType) => {
    const isCancelled = isAppointmentCancelled(appointment);
    const cancelledLabel = `Cancelada: ${formatCancellationReason(appointment.cancellationReason)}`;

    return (
      <button
        className={`${styles.tableActionButton} ${isCancelled ? "" : styles.cancelActionButton}`}
        type="button"
        onClick={() => openCancellationModal(appointment, appointmentType)}
        disabled={isCancelled}
        title={isCancelled ? cancelledLabel : "Cancelar cita"}
      >
        {isCancelled ? cancelledLabel : "Cancelar cita"}
      </button>
    );
  };

  const renderAppointmentsSection = () => (
    <>
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Primeras citas</h2>
          <button
            className={styles.primaryButton}
            type="button"
            onClick={() => openAppointmentModal()}
          >
            Agregar cita
          </button>
        </div>

        {appointmentsError && <div className={styles.alert}>{appointmentsError}</div>}

        <div className={`${styles.tableFilterBar} ${styles.appointmentFilterBar}`} aria-label="Filtros de primeras citas">
          <label className={styles.tableFilterField}>
            <span>Paciente</span>
            <input
              type="search"
              value={appointmentTableFilters.patient}
              onChange={(event) => handleAppointmentTableFilterChange("patient", event.target.value)}
              placeholder="Buscar paciente"
            />
          </label>
          <label className={styles.tableFilterField}>
            <span>Fecha inicial</span>
            <input
              type="date"
              value={appointmentTableFilters.startDate}
              max={appointmentTableFilters.endDate || undefined}
              onChange={(event) => handleAppointmentTableFilterChange("startDate", event.target.value)}
            />
          </label>
          <label className={styles.tableFilterField}>
            <span>Fecha final</span>
            <input
              type="date"
              value={appointmentTableFilters.endDate}
              min={appointmentTableFilters.startDate || undefined}
              onChange={(event) => handleAppointmentTableFilterChange("endDate", event.target.value)}
            />
          </label>
          <label className={styles.tableFilterField}>
            <span>Estado</span>
            <select
              value={appointmentTableFilters.status}
              onChange={(event) => handleAppointmentTableFilterChange("status", event.target.value)}
            >
              <option value="">Todos</option>
              {APPOINTMENT_STATUS_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label className={styles.tableFilterField}>
            <span>Correo</span>
            <input
              type="search"
              value={appointmentTableFilters.email}
              onChange={(event) => handleAppointmentTableFilterChange("email", event.target.value)}
              placeholder="Buscar correo"
            />
          </label>
          <label className={styles.tableFilterField}>
            <span>Área</span>
            <select
              value={appointmentTableFilters.jobArea}
              onChange={(event) => handleAppointmentTableFilterChange("jobArea", event.target.value)}
            >
              <option value="">Todas</option>
              {appointmentJobAreaOptions.map((jobArea) => (
                <option key={jobArea} value={jobArea}>{jobArea}</option>
              ))}
            </select>
          </label>
          <button
            className={styles.clearFiltersButton}
            type="button"
            onClick={() => {
              setAppointmentTableFilters(EMPTY_APPOINTMENT_TABLE_FILTERS);
              setAppointmentTablePage(1);
              setExpandedAppointmentId("");
            }}
          >
            Limpiar filtros
          </button>
        </div>

        <div className={styles.tableCard}>
          {appointmentsLoading ? (
            <div className={styles.tableState}>Cargando citas...</div>
          ) : appointments.length === 0 ? (
            <div className={styles.tableState}>No hay primeras citas registradas.</div>
          ) : filteredAppointments.length === 0 ? (
            <div className={styles.tableState}>No hay primeras citas que coincidan con los filtros.</div>
          ) : (
            <>
              <div className={styles.tableScroll}>
                <table className={styles.patientTable}>
                <thead>
                  <tr>
                    <th>Paciente</th>
                    <th>Fecha y hora</th>
                    <th>Estado</th>
                    <th>Correo</th>
                    <th>Area</th>
                    <th>Review</th>
                    <th>Detalle</th>
                    <th>Cancelar</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedAppointments.map((appointment) => (
                    <React.Fragment key={appointment._id}>
                      <tr className={expandedAppointmentId === appointment._id ? styles.expandedRow : ""}>
                        <td><strong>{appointment.patientName}</strong></td>
                        <td>{formatDateTime(appointment.date)}</td>
                        <td>{formatStatus(appointment.status)}</td>
                        <td>{appointment.patientEmail || "Sin registro"}</td>
                        <td>{appointment.patientJobArea || "Sin registro"}</td>
                        <td>
                          <button
                            className={styles.tableActionButton}
                            type="button"
                            onClick={() => handleCopyReviewUrl(appointment, "checkup")}
                            disabled={copyingReviewUrlId === appointment._id || isAppointmentCancelled(appointment)}
                          >
                            {copyingReviewUrlId === appointment._id ? "Copiando..." : "ReviewUrl"}
                          </button>
                        </td>
                        <td>
                          <button
                            className={styles.tableActionButton}
                            type="button"
                            onClick={() => toggleAppointmentDetails(appointment)}
                            aria-expanded={expandedAppointmentId === appointment._id}
                          >
                            {expandedAppointmentId === appointment._id ? "Ocultar" : "Ver detalle"}
                          </button>
                        </td>
                        <td>{renderCancellationAction(appointment, "checkup")}</td>
                      </tr>
                      {expandedAppointmentId === appointment._id && (
                        <tr className={styles.detailRow}>
                          <td colSpan="8">{renderAppointmentDetail(appointment)}</td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
                </table>
              </div>
              {renderTablePagination({
                currentPage: safeAppointmentPage,
                pageCount: appointmentPageCount,
                totalItems: filteredAppointments.length,
                onPageChange: (page) => {
                  setAppointmentTablePage(page);
                  setExpandedAppointmentId("");
                },
              })}
            </>
          )}
        </div>
      </section>

      {isAppointmentModalOpen && (
        <div className={styles.modalBackdrop} role="presentation">
          <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="appointment-modal-title">
            <div className={styles.modalHeader}>
              <div>
                <h2 id="appointment-modal-title" className={styles.modalTitle}>Agregar primera cita</h2>
                <p className={styles.modalSubtitle}>Selecciona un paciente y agenda su primera consulta.</p>
              </div>
              <button className={styles.iconButton} type="button" onClick={closeAppointmentModal} aria-label="Cerrar">
                x
              </button>
            </div>

            {appointmentsError && <div className={styles.alert}>{appointmentsError}</div>}

            <form className={styles.form} onSubmit={handleCreateAppointment}>
              {!lockedAppointmentPatientId && (
                <label className={styles.field}>
                  <span>Tipo de paciente *</span>
                  <select
                    value={appointmentForm.patientMode}
                    onChange={(event) => handleAppointmentPatientModeChange(event.target.value)}
                    required
                  >
                    {appointmentPatientOptions.length > 0 && <option value="existing">Paciente existente</option>}
                    <option value="new">Paciente nuevo</option>
                  </select>
                </label>
              )}

              <label className={styles.field}>
                <span>Sucursal</span>
                <select
                  value={appointmentIsNewPatient ? appointmentNewPatient.branchId : selectedAppointmentPatient?.branchId || ""}
                  disabled={!appointmentIsNewPatient}
                  onChange={(event) => handleAppointmentNewPatientChange("branchId", event.target.value)}
                  required={appointmentIsNewPatient}
                >
                  {!appointmentIsNewPatient ? (
                    <option value={selectedAppointmentPatient?.branchId || ""}>{selectedAppointmentBranchName}</option>
                  ) : (
                    <>
                      <option value="">Selecciona sucursal</option>
                      {branches.map((branch) => (
                        <option key={branch._id} value={branch._id}>{branch.name || branch._id}</option>
                      ))}
                    </>
                  )}
                </select>
              </label>

              {!appointmentIsNewPatient && (
                <label className={styles.field}>
                  <span>Paciente *</span>
                  <select
                    value={appointmentForm.patientId}
                    onChange={(event) => handleAppointmentFormChange("patientId", event.target.value)}
                    required
                    disabled={Boolean(lockedAppointmentPatientId)}
                  >
                    <option value="">Selecciona paciente</option>
                    {appointmentPatientOptions.map((patient) => (
                      <option key={patient._id} value={patient._id}>{patient.name}</option>
                    ))}
                  </select>
                </label>
              )}

              <label className={styles.field}>
                <span>Fecha y hora *</span>
                <input
                  type="datetime-local"
                  value={appointmentForm.date}
                  onChange={(event) => handleAppointmentFormChange("date", event.target.value)}
                  required
                />
              </label>

              {appointmentIsNewPatient && (
                <details className={styles.optionalSection} open>
                  <summary>Datos del paciente nuevo</summary>
                  <div className={styles.optionalGrid}>
                    <label className={styles.field}>
                      <span>Nombre completo *</span>
                      <input
                        type="text"
                        value={appointmentNewPatient.name}
                        onChange={(event) => handleAppointmentNewPatientChange("name", event.target.value)}
                        required
                      />
                    </label>
                    <label className={styles.field}>
                      <span>Genero *</span>
                      <select
                        value={appointmentNewPatient.gender}
                        onChange={(event) => handleAppointmentNewPatientChange("gender", event.target.value)}
                        required
                      >
                        <option value="">Selecciona genero</option>
                        <option value="femenino">Femenino</option>
                        <option value="masculino">Masculino</option>
                      </select>
                    </label>
                    <label className={styles.field}>
                      <span>Correo institucional</span>
                      <input
                        type="email"
                        value={appointmentNewPatient.email}
                        onChange={(event) => handleAppointmentNewPatientChange("email", event.target.value)}
                        aria-invalid={appointmentNewPatientEmailIsPublic}
                      />
                      {appointmentNewPatientEmailIsPublic && (
                        <small className={styles.fieldError}>No se permiten correos de dominios públicos.</small>
                      )}
                    </label>
                    <label className={styles.field}>
                      <span>ID colaborador</span>
                      <input
                        type="text"
                        value={appointmentNewPatient.externalId}
                        onChange={(event) => handleAppointmentNewPatientChange("externalId", event.target.value)}
                      />
                    </label>
                    <label className={styles.field}>
                      <span>Fecha de nacimiento</span>
                      <input
                        type="date"
                        value={appointmentNewPatient.birthDate}
                        onChange={(event) => handleAppointmentNewPatientChange("birthDate", event.target.value)}
                      />
                    </label>
                    <label className={styles.field}>
                      <span>Area de trabajo</span>
                      <select
                        value={appointmentNewPatient.jobArea}
                        onChange={(event) => handleAppointmentNewPatientChange("jobArea", event.target.value)}
                      >
                        <option value="">Selecciona area</option>
                        {appointmentNewPatientJobAreas.map((jobArea) => (
                          <option key={jobArea} value={jobArea}>{jobArea}</option>
                        ))}
                      </select>
                    </label>
                    <label className={`${styles.field} ${styles.fieldFull}`}>
                      <span>Comentarios</span>
                      <textarea
                        rows="2"
                        value={appointmentNewPatient.comments}
                        onChange={(event) => handleAppointmentNewPatientChange("comments", event.target.value)}
                      />
                    </label>
                  </div>
                </details>
              )}

              <details className={styles.optionalSection}>
                <summary>Datos opcionales de primera consulta</summary>
                <div className={styles.optionalGrid}>
                  <label className={styles.field}>
                    <span>Modalidad</span>
                    <select
                      value={appointmentForm.modality}
                      onChange={(event) => handleAppointmentFormChange("modality", event.target.value)}
                    >
                      <option value="">Selecciona modalidad</option>
                      {MODALITY_OPTIONS.map((modality) => (
                        <option key={modality} value={modality}>{modality}</option>
                      ))}
                    </select>
                  </label>

                  <label className={styles.field}>
                    <span>Peso inicial (kg)</span>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={appointmentForm.weight}
                      onChange={(event) => handleAppointmentFormChange("weight", event.target.value)}
                    />
                  </label>

                  <label className={styles.field}>
                    <span>Estatura (m)</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={appointmentForm.height}
                      onChange={(event) => handleAppointmentFormChange("height", event.target.value)}
                    />
                  </label>

                  <label className={styles.field}>
                    <span>Peso meta (kg)</span>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={appointmentForm.targetWeight}
                      onChange={(event) => handleAppointmentFormChange("targetWeight", event.target.value)}
                    />
                  </label>

                  <label className={styles.field}>
                    <span>% grasa inicial</span>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={appointmentForm.bodyFatPercentage}
                      onChange={(event) => handleAppointmentFormChange("bodyFatPercentage", event.target.value)}
                    />
                  </label>

                  <label className={styles.field}>
                    <span>Grasa visceral</span>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={appointmentForm.visceralFat}
                      onChange={(event) => handleAppointmentFormChange("visceralFat", event.target.value)}
                    />
                  </label>

                  <label className={styles.field}>
                    <span>% musculo inicial</span>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={appointmentForm.musclePercentage}
                      onChange={(event) => handleAppointmentFormChange("musclePercentage", event.target.value)}
                    />
                  </label>

                  <label className={styles.field}>
                    <span>Cintura inicial (cm)</span>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={appointmentForm.waistCm}
                      onChange={(event) => handleAppointmentFormChange("waistCm", event.target.value)}
                    />
                  </label>

                  <fieldset className={`${styles.optionGroup} ${styles.fieldFull}`}>
                    <span>Motivo de consulta</span>
                    <div className={styles.optionGrid}>
                      {CONSULTATION_REASON_OPTIONS.map((reason) => {
                        const selectedReasons = appointmentForm.consultationReasons
                          ? appointmentForm.consultationReasons.split(",").map((item) => item.trim()).filter(Boolean)
                          : [];
                        return (
                          <label className={styles.optionCheck} key={reason}>
                            <input
                              type="checkbox"
                              checked={selectedReasons.includes(reason)}
                              onChange={() => handleMultiOptionToggle("consultationReasons", reason)}
                            />
                            <span>{reason}</span>
                          </label>
                        );
                      })}
                    </div>
                  </fieldset>

                  <fieldset className={`${styles.optionGroup} ${styles.fieldFull}`}>
                    <span>Objetivo del paciente</span>
                    <div className={styles.optionGrid}>
                      {PATIENT_OBJECTIVE_OPTIONS.map((objective) => {
                        const selectedObjectives = appointmentForm.patientObjective
                          ? appointmentForm.patientObjective.split(",").map((item) => item.trim()).filter(Boolean)
                          : [];
                        return (
                          <label className={styles.optionCheck} key={objective}>
                            <input
                              type="checkbox"
                              checked={selectedObjectives.includes(objective)}
                              onChange={() => handleMultiOptionToggle("patientObjective", objective)}
                            />
                            <span>{objective}</span>
                          </label>
                        );
                      })}
                    </div>
                  </fieldset>

                  <label className={`${styles.field} ${styles.fieldFull}`}>
                    <span>Notas</span>
                    <textarea
                      rows="2"
                      value={appointmentForm.notes}
                      onChange={(event) => handleAppointmentFormChange("notes", event.target.value)}
                    />
                  </label>

                  <label className={`${styles.field} ${styles.fieldFull}`}>
                    <span>Diagnosticos medicos</span>
                    <textarea
                      rows="2"
                      value={appointmentForm.medicalDiagnoses}
                      onChange={(event) => handleAppointmentFormChange("medicalDiagnoses", event.target.value)}
                    />
                  </label>

                  <label className={`${styles.checkboxField} ${styles.fieldFull}`}>
                    <input
                      type="checkbox"
                      checked={appointmentForm.planSent}
                      onChange={(event) => handleAppointmentFormChange("planSent", event.target.checked)}
                    />
                    <span>Plan enviado</span>
                  </label>
                </div>
              </details>

              <div className={styles.modalActions}>
                <button className={styles.secondaryButton} type="button" onClick={closeAppointmentModal}>
                  Cancelar
                </button>
                <button
                  className={styles.primaryButton}
                  type="submit"
                  disabled={savingAppointment || (!appointmentIsNewPatient && appointmentPatientOptions.length === 0) || (appointmentIsNewPatient && appointmentNewPatientEmailIsPublic)}
                >
                  {savingAppointment ? "Guardando..." : "Guardar cita"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );

  const renderFollowAppointmentsSection = () => (
    <>
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Citas de seguimiento</h2>
          <button
            className={styles.primaryButton}
            type="button"
            onClick={() => openFollowModal()}
            disabled={followEligiblePatients.length === 0}
            title={followEligiblePatients.length === 0 ? "No hay pacientes con primera cita registrada" : undefined}
          >
            Agregar seguimiento
          </button>
        </div>

        {followAppointmentsError && <div className={styles.alert}>{followAppointmentsError}</div>}

        <div className={`${styles.tableFilterBar} ${styles.appointmentFilterBar}`} aria-label="Filtros de citas de seguimiento">
          <label className={styles.tableFilterField}>
            <span>Paciente</span>
            <input
              type="search"
              value={followTableFilters.patient}
              onChange={(event) => handleFollowTableFilterChange("patient", event.target.value)}
              placeholder="Buscar paciente"
            />
          </label>
          <label className={styles.tableFilterField}>
            <span>Fecha inicial</span>
            <input
              type="date"
              value={followTableFilters.startDate}
              max={followTableFilters.endDate || undefined}
              onChange={(event) => handleFollowTableFilterChange("startDate", event.target.value)}
            />
          </label>
          <label className={styles.tableFilterField}>
            <span>Fecha final</span>
            <input
              type="date"
              value={followTableFilters.endDate}
              min={followTableFilters.startDate || undefined}
              onChange={(event) => handleFollowTableFilterChange("endDate", event.target.value)}
            />
          </label>
          <label className={styles.tableFilterField}>
            <span>Estado</span>
            <select
              value={followTableFilters.status}
              onChange={(event) => handleFollowTableFilterChange("status", event.target.value)}
            >
              <option value="">Todos</option>
              {APPOINTMENT_STATUS_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label className={styles.tableFilterField}>
            <span>Correo</span>
            <input
              type="search"
              value={followTableFilters.email}
              onChange={(event) => handleFollowTableFilterChange("email", event.target.value)}
              placeholder="Buscar correo"
            />
          </label>
          <label className={styles.tableFilterField}>
            <span>Área</span>
            <select
              value={followTableFilters.jobArea}
              onChange={(event) => handleFollowTableFilterChange("jobArea", event.target.value)}
            >
              <option value="">Todas</option>
              {followJobAreaOptions.map((jobArea) => (
                <option key={jobArea} value={jobArea}>{jobArea}</option>
              ))}
            </select>
          </label>
          <button
            className={styles.clearFiltersButton}
            type="button"
            onClick={() => {
              setFollowTableFilters(EMPTY_APPOINTMENT_TABLE_FILTERS);
              setFollowTablePage(1);
              setExpandedFollowAppointmentId("");
            }}
          >
            Limpiar filtros
          </button>
        </div>

        <div className={styles.tableCard}>
          {followAppointmentsLoading ? (
            <div className={styles.tableState}>Cargando citas de seguimiento...</div>
          ) : followAppointments.length === 0 ? (
            <div className={styles.tableState}>
              {followEligiblePatients.length === 0
                ? "No hay pacientes con primera cita activa registrada."
                : "No hay citas de seguimiento registradas."}
            </div>
          ) : filteredFollowAppointments.length === 0 ? (
            <div className={styles.tableState}>No hay seguimientos que coincidan con los filtros.</div>
          ) : (
            <>
              <div className={styles.tableScroll}>
                <table className={styles.patientTable}>
                <thead>
                  <tr>
                    <th>Paciente</th>
                    <th>Fecha y hora</th>
                    <th>Consulta</th>
                    <th>Estado</th>
                    <th>Correo</th>
                    <th>Area</th>
                    <th>Review</th>
                    <th>Detalle</th>
                    <th>Cancelar</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedFollowAppointments.map((followAppointment) => (
                    <React.Fragment key={followAppointment._id}>
                      <tr className={expandedFollowAppointmentId === followAppointment._id ? styles.expandedRow : ""}>
                        <td><strong>{followAppointment.patientName}</strong></td>
                        <td>{formatDateTime(followAppointment.date)}</td>
                        <td>{followAppointment.consultationNumber || "Sin registro"}</td>
                        <td>{formatStatus(getAppointmentStatusValue(followAppointment, { followUp: true }))}</td>
                        <td>{followAppointment.patientEmail || "Sin registro"}</td>
                        <td>{followAppointment.patientJobArea || "Sin registro"}</td>
                        <td>
                          <button
                            className={styles.tableActionButton}
                            type="button"
                            onClick={() => handleCopyReviewUrl(followAppointment, "followCheckup")}
                            disabled={copyingReviewUrlId === followAppointment._id || isAppointmentCancelled(followAppointment)}
                          >
                            {copyingReviewUrlId === followAppointment._id ? "Copiando..." : "ReviewUrl"}
                          </button>
                        </td>
                        <td>
                          <button
                            className={styles.tableActionButton}
                            type="button"
                            onClick={() => toggleFollowAppointmentDetails(followAppointment)}
                            aria-expanded={expandedFollowAppointmentId === followAppointment._id}
                          >
                            {expandedFollowAppointmentId === followAppointment._id ? "Ocultar" : "Ver detalle"}
                          </button>
                        </td>
                        <td>{renderCancellationAction(followAppointment, "followCheckup")}</td>
                      </tr>
                      {expandedFollowAppointmentId === followAppointment._id && (
                        <tr className={styles.detailRow}>
                          <td colSpan="9">{renderFollowDetail(followAppointment)}</td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
                </table>
              </div>
              {renderTablePagination({
                currentPage: safeFollowPage,
                pageCount: followPageCount,
                totalItems: filteredFollowAppointments.length,
                onPageChange: (page) => {
                  setFollowTablePage(page);
                  setExpandedFollowAppointmentId("");
                },
              })}
            </>
          )}
        </div>
      </section>

      {isFollowModalOpen && (
        <div className={styles.modalBackdrop} role="presentation">
          <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="follow-modal-title">
            <div className={styles.modalHeader}>
              <div>
                <h2 id="follow-modal-title" className={styles.modalTitle}>Agregar cita seguimiento</h2>
                <p className={styles.modalSubtitle}>Solo aparecen pacientes con primera cita registrada.</p>
              </div>
              <button className={styles.iconButton} type="button" onClick={closeFollowModal} aria-label="Cerrar">
                x
              </button>
            </div>

            {followAppointmentsError && <div className={styles.alert}>{followAppointmentsError}</div>}

            <form className={styles.form} onSubmit={handleCreateFollowAppointment}>
              <label className={styles.field}>
                <span>Sucursal</span>
                <select value={selectedFollowPatient?.branchId || ""} disabled>
                  <option value={selectedFollowPatient?.branchId || ""}>{selectedFollowBranchName}</option>
                </select>
              </label>

              {renderFollowPlainFields({
                draft: followForm,
                includePatient: true,
                onChange: handleFollowFormChange,
              })}

              <div className={styles.modalActions}>
                <button className={styles.secondaryButton} type="button" onClick={closeFollowModal}>
                  Cancelar
                </button>
                <button className={styles.primaryButton} type="submit" disabled={savingFollowAppointment}>
                  {savingFollowAppointment ? "Guardando..." : "Guardar seguimiento"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );

  const renderPatientInfo = () => {
    if (!detailPatient) return null;

    return (
      <section className={styles.section}>
        <div className={styles.detailToolbar}>
          <button className={styles.secondaryButton} type="button" onClick={goBackToPatients}>
            Regresar
          </button>
          <div className={styles.detailActions}>
            {!detailAppointments.some((appointment) => !isAppointmentCancelled(appointment)) ? (
              <button
                className={styles.primaryButton}
                type="button"
                onClick={() => openAppointmentModal(detailPatient._id)}
              >
                Agregar primera cita
              </button>
            ) : (
              <button
                className={styles.primaryButton}
                type="button"
                onClick={() => openFollowModal(detailPatient._id)}
              >
                Agregar seguimiento
              </button>
            )}
          </div>
        </div>

        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <span>Nombre</span>
            <strong>{detailPatient.name || "Sin registro"}</strong>
          </div>
          <div className={styles.infoItem}>
            <span>Correo</span>
            <strong>{detailPatient.email || "Sin registro"}</strong>
          </div>
          <div className={styles.infoItem}>
            <span>Genero</span>
            <strong>{formatGender(detailPatient.gender)}</strong>
          </div>
          <div className={styles.infoItem}>
            <span>Edad</span>
            <strong>{detailPatient.age ?? "Sin registro"}</strong>
          </div>
          <div className={styles.infoItem}>
            <span>Area</span>
            <strong>{detailPatient.jobArea || "Sin registro"}</strong>
          </div>
          <div className={styles.infoItem}>
            <span>Sucursal</span>
            <strong>{detailBranch?.name || branchNameById[String(detailPatient.branchId)] || "Sucursal"}</strong>
          </div>
          <div className={styles.infoItem}>
            <span>ID colaborador</span>
            <strong>{detailPatient.externalId || "Sin registro"}</strong>
          </div>
          <div className={styles.infoItem}>
            <span>Fecha nacimiento</span>
            <strong>{detailPatient.birthDate ? formatDateTime(detailPatient.birthDate).split(",")[0] : "Sin registro"}</strong>
          </div>
        </div>
      </section>
    );
  };

  const renderPatientAppointmentTable = () => (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Primeras citas</h2>
      </div>
      <div className={styles.tableCard}>
        {detailAppointments.length === 0 ? (
          <div className={styles.tableState}>No hay primeras citas registradas.</div>
        ) : (
          <div className={styles.tableScroll}>
            <table className={styles.patientTable}>
              <thead>
                <tr>
                  <th>Fecha y hora</th>
                  <th>Modalidad</th>
                  <th>Status</th>
                  <th>IMC</th>
                  <th>Review</th>
                  <th>Detalle</th>
                  <th>Cancelar</th>
                </tr>
              </thead>
              <tbody>
                {detailAppointments.map((appointment) => (
                  <React.Fragment key={appointment._id}>
                    <tr className={expandedAppointmentId === appointment._id ? styles.expandedRow : ""}>
                      <td>{formatDateTime(appointment.date)}</td>
                      <td>{appointment.modality || "Sin registro"}</td>
                      <td>{formatStatus(appointment.status)}</td>
                      <td>{formatCheckUpValue(appointment.imc)}</td>
                      <td>
                        <button
                          className={styles.tableActionButton}
                          type="button"
                          onClick={() => handleCopyReviewUrl(appointment, "checkup")}
                          disabled={copyingReviewUrlId === appointment._id || isAppointmentCancelled(appointment)}
                        >
                          {copyingReviewUrlId === appointment._id ? "Copiando..." : "ReviewUrl"}
                        </button>
                      </td>
                      <td>
                        <button
                          className={styles.tableActionButton}
                          type="button"
                          onClick={() => toggleAppointmentDetails(appointment)}
                          aria-expanded={expandedAppointmentId === appointment._id}
                        >
                          {expandedAppointmentId === appointment._id ? "Ocultar" : "Ver detalle"}
                        </button>
                      </td>
                      <td>{renderCancellationAction(appointment, "checkup")}</td>
                    </tr>
                    {expandedAppointmentId === appointment._id && (
                      <tr className={styles.detailRow}>
                        <td colSpan="7">{renderAppointmentDetail(appointment)}</td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );

  const renderPatientFollowTable = () => (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Citas seguimiento</h2>
      </div>
      <div className={styles.tableCard}>
        {detailFollowAppointments.length === 0 ? (
          <div className={styles.tableState}>No hay citas de seguimiento registradas.</div>
        ) : (
          <div className={styles.tableScroll}>
            <table className={styles.patientTable}>
              <thead>
                <tr>
                  <th>Fecha y hora</th>
                  <th>Consulta</th>
                  <th>Modalidad</th>
                  <th>Peso</th>
                  <th>Review</th>
                  <th>Detalle</th>
                  <th>Cancelar</th>
                </tr>
              </thead>
              <tbody>
                {detailFollowAppointments.map((followAppointment) => (
                  <React.Fragment key={followAppointment._id}>
                    <tr className={expandedFollowAppointmentId === followAppointment._id ? styles.expandedRow : ""}>
                      <td>{formatDateTime(followAppointment.date)}</td>
                      <td>{followAppointment.consultationNumber || "Sin registro"}</td>
                      <td>{followAppointment.modality || "Sin registro"}</td>
                      <td>{formatCheckUpValue(followAppointment.weight)}</td>
                      <td>
                        <button
                          className={styles.tableActionButton}
                          type="button"
                          onClick={() => handleCopyReviewUrl(followAppointment, "followCheckup")}
                          disabled={copyingReviewUrlId === followAppointment._id || isAppointmentCancelled(followAppointment)}
                        >
                          {copyingReviewUrlId === followAppointment._id ? "Copiando..." : "ReviewUrl"}
                        </button>
                      </td>
                      <td>
                        <button
                          className={styles.tableActionButton}
                          type="button"
                          onClick={() => toggleFollowAppointmentDetails(followAppointment)}
                          aria-expanded={expandedFollowAppointmentId === followAppointment._id}
                        >
                          {expandedFollowAppointmentId === followAppointment._id ? "Ocultar" : "Ver detalle"}
                        </button>
                      </td>
                      <td>{renderCancellationAction(followAppointment, "followCheckup")}</td>
                    </tr>
                    {expandedFollowAppointmentId === followAppointment._id && (
                      <tr className={styles.detailRow}>
                        <td colSpan="7">{renderFollowDetail(followAppointment)}</td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );

  const renderPatientDetailSection = () => {
    if (detailLoading) {
      return <div className={styles.tableState}>Cargando detalle del paciente...</div>;
    }

    if (detailError) {
      return (
        <section className={styles.section}>
          <button className={styles.secondaryButton} type="button" onClick={goBackToPatients}>
            Regresar
          </button>
          <div className={styles.alert}>{detailError}</div>
        </section>
      );
    }

    return (
      <>
        {renderPatientInfo()}
        {renderPatientAppointmentTable()}
        {renderPatientFollowTable()}
      </>
    );
  };

  const renderPatientDetailModals = () => (
    <>
      {isAppointmentModalOpen && (
        <div className={styles.modalBackdrop} role="presentation">
          <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="detail-appointment-modal-title">
            <div className={styles.modalHeader}>
              <div>
                <h2 id="detail-appointment-modal-title" className={styles.modalTitle}>Agregar primera cita</h2>
                <p className={styles.modalSubtitle}>La cita quedara registrada para este paciente.</p>
              </div>
              <button className={styles.iconButton} type="button" onClick={closeAppointmentModal} aria-label="Cerrar">
                x
              </button>
            </div>

            <form className={styles.form} onSubmit={handleCreateAppointment}>
              <label className={styles.field}>
                <span>Sucursal</span>
                <select value={selectedAppointmentPatient?.branchId || ""} disabled>
                  <option value={selectedAppointmentPatient?.branchId || ""}>{selectedAppointmentBranchName}</option>
                </select>
              </label>

              <label className={styles.field}>
                <span>Paciente *</span>
                <select
                  value={appointmentForm.patientId}
                  onChange={(event) => handleAppointmentFormChange("patientId", event.target.value)}
                  required
                  disabled={Boolean(lockedAppointmentPatientId)}
                >
                  {appointmentPatientOptions.map((patient) => (
                    <option key={patient._id} value={patient._id}>{patient.name}</option>
                  ))}
                </select>
              </label>

              <label className={styles.field}>
                <span>Fecha y hora *</span>
                <input
                  type="datetime-local"
                  value={appointmentForm.date}
                  onChange={(event) => handleAppointmentFormChange("date", event.target.value)}
                  required
                />
              </label>

              <details className={styles.optionalSection}>
                <summary>Datos opcionales de primera consulta</summary>
                <div className={styles.optionalGrid}>
                  <label className={styles.field}>
                    <span>Modalidad</span>
                    <select
                      value={appointmentForm.modality}
                      onChange={(event) => handleAppointmentFormChange("modality", event.target.value)}
                    >
                      <option value="">Selecciona modalidad</option>
                      {MODALITY_OPTIONS.map((modality) => (
                        <option key={modality} value={modality}>{modality}</option>
                      ))}
                    </select>
                  </label>
                  {[
                    ["weight", "Peso inicial (kg)", "0.1"],
                    ["height", "Estatura (m)", "0.01"],
                    ["targetWeight", "Peso meta (kg)", "0.1"],
                    ["bodyFatPercentage", "% grasa inicial", "0.1"],
                    ["visceralFat", "Grasa visceral", "0.1"],
                    ["musclePercentage", "% musculo inicial", "0.1"],
                    ["waistCm", "Cintura inicial (cm)", "0.1"],
                  ].map(([field, label, step]) => (
                    <label className={styles.field} key={field}>
                      <span>{label}</span>
                      <input
                        type="number"
                        min="0"
                        step={step}
                        value={appointmentForm[field]}
                        onChange={(event) => handleAppointmentFormChange(field, event.target.value)}
                      />
                    </label>
                  ))}
                  {renderMultiOptionGroup({
                    label: "Motivo de consulta",
                    field: "consultationReasons",
                    options: CONSULTATION_REASON_OPTIONS,
                    value: appointmentForm.consultationReasons,
                    onToggle: handleMultiOptionToggle,
                  })}
                  {renderMultiOptionGroup({
                    label: "Objetivo del paciente",
                    field: "patientObjective",
                    options: PATIENT_OBJECTIVE_OPTIONS,
                    value: appointmentForm.patientObjective,
                    onToggle: handleMultiOptionToggle,
                  })}
                  <label className={`${styles.field} ${styles.fieldFull}`}>
                    <span>Notas</span>
                    <textarea
                      rows="2"
                      value={appointmentForm.notes}
                      onChange={(event) => handleAppointmentFormChange("notes", event.target.value)}
                    />
                  </label>
                  <label className={`${styles.field} ${styles.fieldFull}`}>
                    <span>Diagnosticos medicos</span>
                    <textarea
                      rows="2"
                      value={appointmentForm.medicalDiagnoses}
                      onChange={(event) => handleAppointmentFormChange("medicalDiagnoses", event.target.value)}
                    />
                  </label>
                  <label className={`${styles.checkboxField} ${styles.fieldFull}`}>
                    <input
                      type="checkbox"
                      checked={appointmentForm.planSent}
                      onChange={(event) => handleAppointmentFormChange("planSent", event.target.checked)}
                    />
                    <span>Plan enviado</span>
                  </label>
                </div>
              </details>

              <div className={styles.modalActions}>
                <button className={styles.secondaryButton} type="button" onClick={closeAppointmentModal}>
                  Cancelar
                </button>
                <button
                  className={styles.primaryButton}
                  type="submit"
                  disabled={savingAppointment || (!appointmentIsNewPatient && appointmentPatientOptions.length === 0) || (appointmentIsNewPatient && appointmentNewPatientEmailIsPublic)}
                >
                  {savingAppointment ? "Guardando..." : "Guardar cita"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isFollowModalOpen && (
        <div className={styles.modalBackdrop} role="presentation">
          <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="detail-follow-modal-title">
            <div className={styles.modalHeader}>
              <div>
                <h2 id="detail-follow-modal-title" className={styles.modalTitle}>Agregar cita seguimiento</h2>
                <p className={styles.modalSubtitle}>La consulta de seguimiento se registrara para este paciente.</p>
              </div>
              <button className={styles.iconButton} type="button" onClick={closeFollowModal} aria-label="Cerrar">
                x
              </button>
            </div>

            <form className={styles.form} onSubmit={handleCreateFollowAppointment}>
              <label className={styles.field}>
                <span>Sucursal</span>
                <select value={selectedFollowPatient?.branchId || ""} disabled>
                  <option value={selectedFollowPatient?.branchId || ""}>{selectedFollowBranchName}</option>
                </select>
              </label>

              {renderFollowPlainFields({
                draft: followForm,
                includePatient: true,
                onChange: handleFollowFormChange,
              })}

              <div className={styles.modalActions}>
                <button className={styles.secondaryButton} type="button" onClick={closeFollowModal}>
                  Cancelar
                </button>
                <button className={styles.primaryButton} type="submit" disabled={savingFollowAppointment}>
                  {savingFollowAppointment ? "Guardando..." : "Guardar seguimiento"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );

  const renderCancellationModal = () => {
    if (!cancellationTarget) return null;

    const { appointment, appointmentType } = cancellationTarget;
    const appointmentLabel = appointmentType === "checkup" ? "primera cita" : "cita de seguimiento";

    return (
      <div className={styles.modalBackdrop} role="presentation">
        <div
          className={`${styles.modal} ${styles.cancellationModal}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="cancellation-modal-title"
        >
          <div className={styles.modalHeader}>
            <div>
              <h2 id="cancellation-modal-title" className={styles.modalTitle}>Cancelar cita</h2>
              <p className={styles.modalSubtitle}>
                Confirma la cancelación de la {appointmentLabel} de {appointment.patientName || "este paciente"},
                programada para {formatDateTime(appointment.date)}.
              </p>
            </div>
            <button
              className={styles.iconButton}
              type="button"
              onClick={closeCancellationModal}
              disabled={savingCancellation}
              aria-label="Cerrar"
            >
              x
            </button>
          </div>

          {cancellationError && <div className={styles.alert}>{cancellationError}</div>}

          <form className={styles.form} onSubmit={handleCancelAppointment}>
            <label className={`${styles.field} ${styles.fieldFull}`}>
              <span>Motivo de cancelación *</span>
              <select
                value={cancellationReason}
                onChange={(event) => setCancellationReason(event.target.value)}
                required
              >
                <option value="">Selecciona un motivo</option>
                {CANCELLATION_REASON_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>

            <label className={`${styles.field} ${styles.fieldFull}`}>
              <span>Comentarios de cancelación</span>
              <textarea
                rows="4"
                value={cancellationComments}
                onChange={(event) => setCancellationComments(event.target.value)}
                placeholder="Agrega un comentario opcional"
              />
            </label>

            <div className={styles.modalActions}>
              <button
                className={styles.secondaryButton}
                type="button"
                onClick={closeCancellationModal}
                disabled={savingCancellation}
              >
                Regresar
              </button>
              <button
                className={`${styles.primaryButton} ${styles.cancelConfirmButton}`}
                type="submit"
                disabled={savingCancellation || !cancellationReason}
              >
                {savingCancellation ? "Cancelando..." : "Confirmar cancelación"}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.shell}>
      <NutriologyHeader />
      <NutriologySidebar activeSection={isPatientDetail ? "patients" : activeSection} onSectionChange={handleSectionChange} />
      <main className={styles.main}>
        <section className={styles.pageHeader}>
          <div>
            <h1 className={styles.pageTitle}>{section.title}</h1>
            <p className={styles.pageSubtitle}>{section.subtitle}</p>
          </div>
          {renderBranchSelector()}
        </section>

        {!isPatientDetail && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Resumen</h2>
          <div className={styles.cardGrid}>
            <article className={styles.card}>
              <span className={styles.cardValue}>{summaryLoading ? "..." : summary.patientsCount}</span>
              <span className={styles.cardLabel}>Pacientes</span>
            </article>
            <article className={styles.card}>
              <span className={styles.cardValue}>{summaryLoading ? "..." : summary.firstCheckUpsCount}</span>
              <span className={styles.cardLabel}>Primeras Consultas</span>
            </article>
            <article className={styles.card}>
              <span className={styles.cardValue}>{summaryLoading ? "..." : summary.followCheckUpsCount}</span>
              <span className={styles.cardLabel}>Seguimientos</span>
            </article>
          </div>
        </section>
        )}

        {isPatientDetail ? (
          renderPatientDetailSection()
        ) : activeSection === "patients" ? (
          renderPatientsSection()
        ) : activeSection === "appointments" ? (
          renderAppointmentsSection()
        ) : activeSection === "followAppointments" ? (
          renderFollowAppointmentsSection()
        ) : activeSection === "historicalUpload" ? (
          renderHistoricalUploadSection()
        ) : activeSection === "general" ? (
          renderGeneralSection()
        ) : activeSection === "generalEvaluation" ? (
          renderGeneralEvaluationSection()
        ) : (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Datos</h2>
            <div className={styles.emptyState}>{section.empty}</div>
          </section>
        )}
        {isPatientDetail && renderPatientDetailModals()}
        {renderCancellationModal()}
      </main>
    </div>
  );
}
